﻿import { useState, useEffect } from 'react';
import axios from 'axios';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { format, parseISO } from 'date-fns';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  MagnifyingGlassIcon,
  FunnelIcon,
  XMarkIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  ArrowsRightLeftIcon,
  BanknotesIcon,
  BuildingLibraryIcon,
  ArrowRightCircleIcon,
  ArrowUpTrayIcon,
  ArrowLeftIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  TableCellsIcon
} from '@heroicons/react/24/outline';
import ExportButtons from '../components/ExportButtons';
import DateQuickPresets from '../components/DateQuickPresets';

const CREATE_PLAN_OPTION_VALUE = '__create_new_plan__';
const DRE_GROUP_OPTIONS = [
  'Receita Operacional',
  'Receita Não Operacional',
  'Deduções',
  'Custos',
  'Despesas Operacionais',
  'Despesas Não Operacionais',
  'Despesas Tributárias',
  'Despesas Financeiras',
  'Resultado',
];
const IMPORT_ROW_OVERRIDE_FIELDS = [
  'tipo',
  'plano_conta_id',
  'centro_custo_id',
  'conta_bancaria_id',
  'conta_destino_id',
  'direcao_caixa',
  'status',
];

const Lancamentos = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const toNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const [lancamentos, setLancamentos] = useState([]);
  const [planosContas, setPlanosContas] = useState([]);
  const [centrosCustos, setCentrosCustos] = useState([]);
  const [contasBancarias, setContasBancarias] = useState([]);
  const [showTipoModal, setShowTipoModal] = useState(false); // Modal para escolher tipo
  const [showModal, setShowModal] = useState(false); // Modal do formul?rio
  const [showTransferModal, setShowTransferModal] = useState(false); // Modal de transfer?ncia
  const [manterFormularioAberto, setManterFormularioAberto] = useState(false);
  const [usarRateioCentro, setUsarRateioCentro] = useState(false);
  const [rateiosCentro, setRateiosCentro] = useState([{ centro_custo_id: '', percentual: 100 }]);
  const [composicoes, setComposicoes] = useState([
    { tipo: 'patrimonial', plano_conta_id: '', centro_custo_id: '', valor: '', descricao: 'Principal' },
    { tipo: 'despesa', plano_conta_id: '', centro_custo_id: '', valor: '', descricao: 'Juros/encargos' },
  ]);
  const [tipoSelecionado, setTipoSelecionado] = useState(''); // 'lancamento' ou 'transferencia'
  const [editingId, setEditingId] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [filtros, setFiltros] = useState({
    data_inicio: format(new Date().setMonth(new Date().getMonth() - 1), 'yyyy-MM-dd'),
    data_fim: format(new Date(), 'yyyy-MM-dd'),
    descricao: '',
    tipo: 'todos',
    plano_conta_id: 'todos',
    centro_custo_id: 'todos',
    conta_bancaria_id: 'todas'
  });
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importFileBase64, setImportFileBase64] = useState('');
  const [importPreview, setImportPreview] = useState(null);
  const [importRows, setImportRows] = useState([]);
  const [importLotes, setImportLotes] = useState([]);
  const [importProfiles, setImportProfiles] = useState([]);
  const [importStatusFilter, setImportStatusFilter] = useState('all');
  const [importSearchTerm, setImportSearchTerm] = useState('');
  const [importandoArquivo, setImportandoArquivo] = useState(false);
  const [importandoConfirmacao, setImportandoConfirmacao] = useState(false);
  const [exportandoContabil, setExportandoContabil] = useState(false);
  const [savingImportMappingRows, setSavingImportMappingRows] = useState([]);
  const [showCreatePlanoModal, setShowCreatePlanoModal] = useState(false);
  const [savingCreatePlano, setSavingCreatePlano] = useState(false);
  const [importPlanoTargetRow, setImportPlanoTargetRow] = useState(null);
  const [importPlanoForm, setImportPlanoForm] = useState({
    codigo: '',
    descricao: '',
    tipo: 'despesa',
    nivel: 1,
    parent_id: '',
    dre_grupo: ''
  });
  const [importConfig, setImportConfig] = useState({
    perfil_banco: 'auto',
    data: '',
    descricao: '',
    valor: '',
    detalhe_historico: '',
    tipo: '',
    conta_bancaria_id: '',
    conta_destino_id: '',
    centro_custo_id: '',
    status: 'confirmado'
  });
  const isImportRoute = location.pathname === '/lancamentos/importar';

  // Formul?rio para lan?amentos normais
  const { register, handleSubmit, reset, formState: { errors }, watch, setValue } = useForm({
    defaultValues: {
      data: format(new Date(), 'yyyy-MM-dd'),
      tipo: 'despesa',
      status: 'confirmado'
    }
  });

  // Formul?rio para transfer?ncias
  const { register: registerTransfer, handleSubmit: handleSubmitTransfer, 
          reset: resetTransfer, formState: { errors: errorsTransfer }, 
          watch: watchTransfer, setValue: setValueTransfer } = useForm({
    defaultValues: {
      data: format(new Date(), 'yyyy-MM-dd'),
      descricao: 'Transferência entre contas',
      valor: 0.01,
      tipo: 'transferencia',
      conta_bancaria_id: '',
      conta_destino_id: '',
      status: 'confirmado'
    }
  });

  const tipoLancamento = watch('tipo');
  const direcaoCaixaLancamento = watch('direcao_caixa') || 'saida';
  const valorLancamentoParaRateio = toNumber(watch('valor'));
  const contaOrigem = watchTransfer('conta_bancaria_id');
  const contaDestino = watchTransfer('conta_destino_id');
  const planosParaLancamento = planosContas.filter((plano) => {
    if (tipoLancamento === 'receita') return plano.tipo === 'receita';
    if (tipoLancamento === 'despesa') return plano.tipo === 'despesa';
    if (tipoLancamento === 'patrimonial') return plano.tipo === 'patrimonio';
    if (tipoLancamento === 'composto') return false;
    return plano.tipo !== 'patrimonio';
  });

  const getPlanoTipoByLancamento = (tipo) => {
    if (tipo === 'patrimonial') return 'patrimonio';
    return tipo;
  };

  const getPlanosByComposicaoTipo = (tipo) => planosContas.filter((plano) => plano.tipo === getPlanoTipoByLancamento(tipo));
  const tiposComposicaoPermitidos = direcaoCaixaLancamento === 'entrada'
    ? [
        { value: 'patrimonial', label: 'Patrimonial' },
        { value: 'receita', label: 'Receita' },
      ]
    : [
        { value: 'patrimonial', label: 'Patrimonial' },
        { value: 'despesa', label: 'Despesa' },
      ];

  const normalizeHeader = (value) => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

  const normalizeImportText = (value) => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

  const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      resolve(result.split(',')[1] || '');
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const getSimilarityMeta = (scoreValue) => {
    const score = Math.round(toNumber(scoreValue) * 100);
    if (score >= 85) return { label: 'Alta', className: 'badge-success', score };
    if (score >= 65) return { label: 'Média', className: 'badge-warning', score };
    if (score > 0) return { label: 'Baixa', className: 'badge-danger', score };
    return { label: 'Sem', className: 'badge-secondary', score: 0 };
  };

  const getQualidadeMeta = (row) => {
    const erros = Array.isArray(row?.erros_validacao) ? row.erros_validacao : [];
    const avisos = Array.isArray(row?.avisos_validacao) ? row.avisos_validacao : [];
    if (row?.skip) return { label: 'Ignorada', className: 'badge-secondary' };
    if (erros.length > 0 || row?.valido === false) return { label: 'Erro', className: 'badge-danger' };
    if (
      avisos.length > 0 ||
      row?.pendente_revisao ||
      (row?.tipo !== 'transferencia' && !row?.plano_conta_id) ||
      (row?.tipo === 'patrimonial' && !row?.direcao_caixa)
    ) {
      return { label: 'Pendente', className: 'badge-warning' };
    }
    return { label: 'Pronta', className: 'badge-success' };
  };

  const recomputeImportRow = (row) => {
    if (!row) return row;

    const avisos = (Array.isArray(row.avisos_validacao) ? row.avisos_validacao : []).filter((warning) => {
      if (warning === 'Sem plano de contas sugerido' && (row.tipo === 'transferencia' || row.plano_conta_id)) return false;
      if (warning === 'Movimento patrimonial sem direcao de caixa' && row.tipo === 'patrimonial' && row.direcao_caixa) return false;
      if (
        warning === 'Transferencia sem conta origem/destino mapeadas' &&
        row.tipo === 'transferencia' &&
        row.conta_bancaria_id &&
        row.conta_destino_id &&
        row.conta_bancaria_id !== row.conta_destino_id
      ) {
        return false;
      }
      if (warning === 'Sem mapeamento de historico' && row.mapeado_por_historico) return false;
      return true;
    });

    const pendenteRevisao = row.tipo === 'transferencia'
      ? (!row.conta_bancaria_id || !row.conta_destino_id || row.conta_bancaria_id === row.conta_destino_id)
      : (!row.plano_conta_id || (row.tipo === 'patrimonial' && !row.direcao_caixa));

    return {
      ...row,
      avisos_validacao: avisos,
      pendente_revisao: pendenteRevisao,
    };
  };

  const isCriticalImportRow = (row) => {
    if (!row || row.skip) return false;
    if (row.valido === false) return true;
    if (row.tipo === 'transferencia') {
      return !row.conta_bancaria_id || !row.conta_destino_id || row.conta_bancaria_id === row.conta_destino_id;
    }
    return !row.plano_conta_id || (row.tipo === 'patrimonial' && !row.direcao_caixa);
  };

  const importSummary = (() => {
    const total = importRows.length;
    const ignoradas = importRows.filter((r) => r.skip).length;
    const prontas = importRows.filter((r) => getQualidadeMeta(r).label === 'Pronta').length;
    const pendentes = importRows.filter((r) => getQualidadeMeta(r).label === 'Pendente').length;
    const erros = importRows.filter((r) => getQualidadeMeta(r).label === 'Erro').length;
    const transferencias = importRows.filter((r) => r.tipo === 'transferencia' && !r.skip).length;
    const patrimoniais = importRows.filter((r) => r.tipo === 'patrimonial' && !r.skip).length;
    const criticas = importRows.filter((r) => isCriticalImportRow(r)).length;
    const importaveis = importRows.filter((r) => !r.skip && !isCriticalImportRow(r)).length;
    return { total, ignoradas, prontas, pendentes, erros, transferencias, patrimoniais, criticas, importaveis };
  })();

  const canConfirmImport = Boolean(importPreview) && !importandoConfirmacao && importSummary.importaveis > 0 && importSummary.criticas === 0;
  const importProfileOptions = importProfiles.length ? importProfiles : [
    { id: 'auto', nome: 'Auto detectar', descricao: 'Detecta automaticamente o layout do arquivo' },
    { id: 'banco_brasil', nome: 'Banco do Brasil', descricao: 'Perfil voltado para extratos do Banco do Brasil' },
    { id: 'generico', nome: 'Generico', descricao: 'Usa um mapeamento generico para outros bancos' },
  ];
  const selectedImportProfile = importProfileOptions.find((perfil) => perfil.id === importConfig.perfil_banco) || importProfileOptions[0];
  const importReviewCount = importSummary.pendentes + importSummary.erros;
  const importFilterOptions = [
    { id: 'all', label: 'Todas', count: importSummary.total },
    { id: 'review', label: 'Revisar', count: importReviewCount },
    { id: 'ready', label: 'Prontas', count: importSummary.prontas },
    { id: 'ignored', label: 'Ignoradas', count: importSummary.ignoradas },
    { id: 'transfer', label: 'Transferencias', count: importSummary.transferencias },
    { id: 'patrimonial', label: 'Patrimoniais', count: importSummary.patrimoniais },
  ];
  const normalizedImportSearchTerm = normalizeImportText(importSearchTerm);
  const filteredImportRows = importRows.filter((row) => {
    const qualityLabel = getQualidadeMeta(row).label;
    const matchesFilter = (() => {
      switch (importStatusFilter) {
        case 'review':
          return qualityLabel === 'Pendente' || qualityLabel === 'Erro';
        case 'ready':
          return qualityLabel === 'Pronta';
        case 'ignored':
          return row.skip === true;
        case 'transfer':
          return row.tipo === 'transferencia' && !row.skip;
        case 'patrimonial':
          return row.tipo === 'patrimonial' && !row.skip;
        default:
          return true;
      }
    })();

    if (!matchesFilter) return false;
    if (!normalizedImportSearchTerm) return true;

    const searchableValues = [
      row.row_number,
      row.data,
      row.descricao,
      row.descricao_base,
      row.detalhe_historico_text,
      row.expressao_mapeamento,
      row.origem_sugestao,
    ];

    return searchableValues.some((value) => normalizeImportText(value).includes(normalizedImportSearchTerm));
  });
  const importOverviewCards = [
    {
      id: 'total',
      label: 'Linhas lidas',
      value: importSummary.total,
      helper: importPreview?.worksheetName ? `Planilha ${importPreview.worksheetName}` : 'Aguardando leitura',
      icon: TableCellsIcon,
      iconClassName: 'bg-slate-900 text-white',
    },
    {
      id: 'ready',
      label: 'Prontas',
      value: importSummary.prontas,
      helper: 'Linhas sem ajuste manual',
      icon: CheckCircleIcon,
      iconClassName: 'bg-success-600 text-white',
    },
    {
      id: 'review',
      label: 'Em revisao',
      value: importReviewCount,
      helper: 'Pendencias ou erros',
      icon: ClockIcon,
      iconClassName: 'bg-warning-500 text-white',
    },
    {
      id: 'critical',
      label: 'Criticas',
      value: importSummary.criticas,
      helper: 'Bloqueiam a importacao',
      icon: ExclamationTriangleIcon,
      iconClassName: 'bg-danger-600 text-white',
    },
    {
      id: 'importable',
      label: 'Importaveis',
      value: importSummary.importaveis,
      helper: 'Linhas liberadas',
      icon: ArrowUpTrayIcon,
      iconClassName: 'bg-primary-600 text-white',
    },
  ];

  const getContaBancariaLabel = (contaId) => {
    if (!contaId) return 'Sem conta';
    const conta = contasBancarias.find((item) => item.id === contaId);
    return conta ? `${conta.banco} - ${conta.conta}` : 'Conta nao encontrada';
  };

  const getImportRowCardClassName = (row) => {
    if (row?.skip) return 'border-gray-200 bg-gray-50';
    const qualityLabel = getQualidadeMeta(row).label;
    if (qualityLabel === 'Erro') return 'border-danger-200 bg-danger-50';
    if (qualityLabel === 'Pendente') return 'border-warning-200 bg-warning-50';
    return 'border-success-200 bg-white';
  };

  const getImportFilterButtonClassName = (filterId) => (
    importStatusFilter === filterId
      ? 'bg-primary-600 text-white border-primary-600 shadow-sm'
      : 'bg-white text-gray-700 border-gray-200 hover:border-primary-200 hover:text-primary-700'
  );

  const getImportOriginMeta = (origin) => {
    const normalized = String(origin || '').trim();
    switch (normalized) {
      case 'historico_detalhe_exact':
        return { label: 'HD ex', title: 'Historico + detalhe exato' };
      case 'historico_detalhe_contains':
        return { label: 'HD ct', title: 'Historico + detalhe contem' };
      case 'detalhe_exact':
        return { label: 'Det ex', title: 'Detalhe exato' };
      case 'detalhe_contains':
        return { label: 'Det ct', title: 'Detalhe contem' };
      case 'historico_exact':
        return { label: 'Hist ex', title: 'Historico exato' };
      case 'historico_contains':
        return { label: 'Hist ct', title: 'Historico contem' };
      case 'legacy_historico_exact':
        return { label: 'Leg ex', title: 'Mapeamento legado exato' };
      case 'legacy_historico_similar':
        return { label: 'Leg sim', title: 'Mapeamento legado similar' };
      default:
        return { label: '-', title: normalized || 'Sem origem de sugestao' };
    }
  };

  const getImportMappingStatusMeta = (row) => {
    if (row?.persist_mapping) return { label: 'Agora', className: 'badge-info', title: 'Regra salva agora nesta importacao' };
    if (row?.mapeado_por_historico) return { label: 'Auto', className: 'badge-success', title: 'Linha preenchida por regra existente' };
    return { label: 'Novo', className: 'badge-warning', title: 'Ainda sem regra persistida' };
  };

  const canPersistImportMapping = (row) => {
    if (!row || row.skip || !normalizeImportText(row.expressao_mapeamento || row.detalhe_historico_text)) return false;
    if (row.tipo === 'transferencia') {
      return Boolean(
        row.conta_bancaria_id &&
        row.conta_destino_id &&
        row.conta_bancaria_id !== row.conta_destino_id
      );
    }
    return Boolean(row.plano_conta_id && (row.tipo !== 'patrimonial' || row.direcao_caixa));
  };

  const hasManualImportOverride = (row) => Boolean(
    row?.manual_override_fields?.plano_conta_id ||
    row?.manual_override_fields?.tipo ||
    row?.manual_override_fields?.direcao_caixa ||
    row?.manual_override_fields?.mapeamento_expressao
  );

  const getImportMappingButtonLabel = (row) => {
    if (row?.persist_mapping) return 'Mapeado';
    if (row?.mapeado_por_historico && hasManualImportOverride(row)) return 'Atualizar Regra';
    if (row?.mapeado_por_historico) return 'Refinar Regra';
    return 'Mapear';
  };

  const buildInitialImportConfig = () => ({
    perfil_banco: 'auto',
    data: '',
    descricao: '',
    valor: '',
    detalhe_historico: '',
    tipo: '',
    conta_bancaria_id: contasBancarias[0]?.id || '',
    conta_destino_id: contasBancarias.find((conta) => conta.id !== contasBancarias[0]?.id)?.id || contasBancarias[1]?.id || '',
    centro_custo_id: centrosCustos[0]?.id || '',
    status: 'confirmado'
  });

  const resetImportState = () => {
    setImportFile(null);
    setImportFileBase64('');
    setImportPreview(null);
    setImportRows([]);
    setImportLotes([]);
    setImportStatusFilter('all');
    setImportSearchTerm('');
    setImportandoArquivo(false);
    setImportandoConfirmacao(false);
    setSavingImportMappingRows([]);
    setImportConfig(buildInitialImportConfig());
  };

  const resetCreatePlanoModal = () => {
    setShowCreatePlanoModal(false);
    setSavingCreatePlano(false);
    setImportPlanoTargetRow(null);
    setImportPlanoForm({
      codigo: '',
      descricao: '',
      tipo: 'despesa',
      nivel: 1,
      parent_id: '',
      dre_grupo: ''
    });
  };

  useEffect(() => {
    if (!isImportRoute) {
      carregarDados();
    }
    carregarDropdowns();
  }, [filtros, isImportRoute]);

  useEffect(() => {
    if (!isImportRoute) return;
    carregarPerfisImportacao();
    carregarLotesImportacao();
  }, [isImportRoute]);

  useEffect(() => {
    if (!isImportRoute) return;
    setImportConfig((prev) => {
      const nextConta = prev.conta_bancaria_id || contasBancarias[0]?.id || '';
      const nextDestino = prev.conta_destino_id
        || contasBancarias.find((conta) => conta.id !== nextConta)?.id
        || contasBancarias[1]?.id
        || '';
      const nextCentro = prev.centro_custo_id || centrosCustos[0]?.id || '';

      if (
        prev.conta_bancaria_id === nextConta &&
        prev.conta_destino_id === nextDestino &&
        prev.centro_custo_id === nextCentro
      ) {
        return prev;
      }

      return {
        ...prev,
        conta_bancaria_id: nextConta,
        conta_destino_id: nextDestino,
        centro_custo_id: nextCentro,
      };
    });
  }, [isImportRoute, contasBancarias, centrosCustos]);

  const carregarDados = async () => {
    setCarregando(true);
    try {
      const response = await axios.get('/api/lancamentos', { params: filtros });
      const data = Array.isArray(response.data) ? response.data : [];
      setLancamentos(data.map((item) => ({
        ...item,
        valor: toNumber(item?.valor)
      })));
    } catch (error) {
      console.error('Erro ao carregar lan?amentos:', error);
      toast.error('Erro ao carregar lançamentos');
    } finally {
      setCarregando(false);
    }
  };

  const carregarDropdowns = async () => {
    try {
      const [planosResponse, centrosResponse, contasResponse] = await Promise.all([
        axios.get('/api/plano-contas'),
        axios.get('/api/centro-custos'),
        axios.get('/api/contas-bancarias')
      ]);
      
      setPlanosContas(planosResponse.data);
      setCentrosCustos(centrosResponse.data);
      setContasBancarias(
        (Array.isArray(contasResponse.data) ? contasResponse.data : []).map((conta) => ({
          ...conta,
          saldo_atual: toNumber(conta?.saldo_atual),
          saldo_inicial: toNumber(conta?.saldo_inicial)
        }))
      );
    } catch (error) {
      console.error('Erro ao carregar dropdowns:', error);
    }
  };

  // Abrir modal de sele??o de tipo
  const handleNovoLancamento = () => {
    setShowTipoModal(true);
  };

  const handleFecharImportacao = (options = {}) => {
    resetCreatePlanoModal();
    if (options.reset !== false) {
      resetImportState();
    }
    navigate('/lancamentos');
  };

  const handleAbrirImportacao = () => {
    resetCreatePlanoModal();
    resetImportState();
    navigate('/lancamentos/importar');
  };

  const handleAbrirEdicaoLote = () => {
    navigate('/lancamentos/edicao-lote');
  };

  const handleAbrirConferenciaSaldo = () => {
    const params = new URLSearchParams();
    if (filtros.data_inicio) params.set('data_inicio', filtros.data_inicio);
    if (filtros.data_fim) params.set('data_fim', filtros.data_fim);
    if (filtros.conta_bancaria_id) params.set('conta_bancaria_id', filtros.conta_bancaria_id);
    navigate(`/relatorios/conferencia-saldo${params.toString() ? `?${params.toString()}` : ''}`);
  };

  const handleExportarContabil = async () => {
    setExportandoContabil(true);
    try {
      const params = {
        data_inicio: filtros.data_inicio,
        data_fim: filtros.data_fim,
        conta_bancaria_id: filtros.conta_bancaria_id !== 'todas' ? filtros.conta_bancaria_id : undefined,
      };

      const preview = await axios.get('/api/contabil/preview', { params });
      const pendencias = Array.isArray(preview.data?.pendencias) ? preview.data.pendencias : [];
      if (pendencias.length > 0) {
        toast.error(`Exportacao bloqueada: ${pendencias.length} pendencia(s) de conta contabil.`);
        return;
      }

      const response = await axios.get('/api/contabil/exportar-excel', {
        params,
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `exportacao_contabil_${filtros.data_inicio || 'inicio'}_${filtros.data_fim || 'fim'}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Exportacao contabil gerada com sucesso.');
    } catch (error) {
      console.error('Erro ao exportar contabil:', error);
      toast.error(error.response?.data?.error || 'Erro ao gerar exportacao contabil');
    } finally {
      setExportandoContabil(false);
    }
  };

  const carregarPerfisImportacao = async () => {
    try {
      const response = await axios.get('/api/lancamentos/importar-xlsx/perfis');
      setImportProfiles(Array.isArray(response.data?.perfis) ? response.data.perfis : []);
    } catch (error) {
      console.error('Erro ao carregar perfis de importacao:', error);
      setImportProfiles([
        { id: 'auto', nome: 'Auto detectar', descricao: 'Detecta automaticamente' },
        { id: 'generico', nome: 'Generico', descricao: 'Padrao' },
      ]);
    }
  };

  const handleSelectImportPlano = (row, nextValue) => {
    if (nextValue === CREATE_PLAN_OPTION_VALUE) {
      const tipoPlano = getPlanoTipoByLancamento(row.tipo) === 'patrimonio' ? 'patrimonio' : (row.tipo === 'receita' ? 'receita' : 'despesa');
      setImportPlanoTargetRow({
        row_number: row.row_number,
        tipo: tipoPlano
      });
      setImportPlanoForm({
        codigo: '',
        descricao: '',
        tipo: tipoPlano,
        nivel: 1,
        parent_id: '',
        dre_grupo: ''
      });
      setShowCreatePlanoModal(true);
      return;
    }

    handleChangeImportRow(row.row_number, 'plano_conta_id', nextValue);
  };

  const handleCreatePlanoFromImport = async () => {
    try {
      if (!importPlanoForm.codigo.trim() || !importPlanoForm.descricao.trim() || !importPlanoForm.tipo) {
        toast.error('Preencha codigo, descricao e tipo do plano.');
        return;
      }

      setSavingCreatePlano(true);
      const payload = {
        codigo: importPlanoForm.codigo.trim(),
        descricao: importPlanoForm.descricao.trim(),
        tipo: importPlanoForm.tipo,
        nivel: Number(importPlanoForm.nivel || 1),
        parent_id: importPlanoForm.parent_id || null,
        dre_grupo: importPlanoForm.dre_grupo || null
      };

      const response = await axios.post('/api/plano-contas', payload);
      const planoCriado = response?.data?.plano || response?.data;
      const planosResponse = await axios.get('/api/plano-contas');
      setPlanosContas(Array.isArray(planosResponse.data) ? planosResponse.data : []);

      if (planoCriado?.id && importPlanoTargetRow?.row_number) {
        handleChangeImportRow(importPlanoTargetRow.row_number, 'plano_conta_id', planoCriado.id);
      }

      toast.success('Plano de contas criado com sucesso.');
      resetCreatePlanoModal();
    } catch (error) {
      console.error('Erro ao criar plano a partir da importacao:', error);
      toast.error(error.response?.data?.error || 'Erro ao criar plano de contas');
      setSavingCreatePlano(false);
    }
  };

  const buildImportMappingPayload = (row) => {
    if (!row) return null;

    const detalheHistorico = String(row.expressao_mapeamento || row.detalhe_historico_text || '').trim();
    const tipoMovimento = row.tipo === 'transferencia' ? 'transferencia' : 'lancamento';

    return {
      tipo_regra: 'detalhe',
      match_mode: 'contains',
      prioridade: 120,
      historico: '',
      detalhe_historico: detalheHistorico,
      tipo_movimento: tipoMovimento,
      tipo: tipoMovimento === 'transferencia' ? 'transferencia' : row.tipo,
      direcao_caixa: row.tipo === 'patrimonial' ? (row.direcao_caixa || null) : null,
      plano_conta_id: tipoMovimento === 'transferencia' ? null : (row.plano_conta_id || null),
      centro_custo_id: row.tipo === 'patrimonial' ? null : (row.centro_custo_id || null),
      conta_bancaria_id: row.conta_bancaria_id || null,
      conta_origem_id: tipoMovimento === 'transferencia' ? (row.conta_bancaria_id || null) : null,
      conta_destino_id: tipoMovimento === 'transferencia' ? (row.conta_destino_id || null) : null,
      transferencia_direcao: 'fixa',
      ativo: true,
    };
  };

  const mergeImportPreviewRows = (parsedRows, previousRows = [], options = {}) => {
    const previousByRow = new Map(
      (Array.isArray(previousRows) ? previousRows : []).map((row) => [Number(row.row_number), row])
    );
    const persistMappingRows = new Set(
      (Array.isArray(options.persistMappingRows) ? options.persistMappingRows : []).map((rowNumber) => Number(rowNumber))
    );

    return (Array.isArray(parsedRows) ? parsedRows : []).map((nextRow) => {
      const previousRow = previousByRow.get(Number(nextRow.row_number));
      const manualOverrideFields = { ...(previousRow?.manual_override_fields || {}) };
      const mergedRow = {
        ...nextRow,
        skip: previousRow?.skip === true,
        persist_mapping: persistMappingRows.has(Number(nextRow.row_number)) || previousRow?.persist_mapping === true,
        manual_override_fields: manualOverrideFields,
        expressao_mapeamento: manualOverrideFields.mapeamento_expressao
          ? String(previousRow?.expressao_mapeamento || '')
          : String(nextRow.detalhe_historico_text || '').trim(),
      };

      IMPORT_ROW_OVERRIDE_FIELDS.forEach((field) => {
        if (manualOverrideFields[field]) {
          mergedRow[field] = previousRow?.[field] ?? mergedRow[field];
        }
      });

      return recomputeImportRow(mergedRow);
    });
  };

  const applyImportPreviewPayload = (payload, options = {}) => {
    const previousRows = Array.isArray(options.previousRows) ? options.previousRows : [];
    const syncDetected = options.syncDetected === true;
    const headers = Array.isArray(payload?.headers) ? payload.headers : [];
    const detected = payload?.detected || {};
    const findHeader = (patterns) => headers.find((header) => patterns.some((pattern) => normalizeHeader(header).includes(pattern))) || '';

    setImportPreview(payload || null);
    setImportRows(mergeImportPreviewRows(payload?.parsedRows, previousRows, options));

    if (!syncDetected) return;

    setImportConfig((prev) => ({
      ...prev,
      perfil_banco: payload?.perfil_banco || prev.perfil_banco || 'auto',
      data: detected.data || findHeader(['data', 'date']),
      descricao: detected.descricao || findHeader(['historico', 'descricao', 'memo', 'lancamento']),
      detalhe_historico: detected.detalhe_historico || findHeader(['detalhe', 'complemento']),
      valor: detected.valor || findHeader(['valor', 'amount', 'quantia']),
      tipo: detected.tipo || findHeader(['inf', 'tipo', 'natureza', 'debito', 'credito'])
    }));
  };

  const requestImportPreview = async (base64, worksheetName) => {
    const response = await axios.post('/api/lancamentos/importar-xlsx/preview', {
      fileBase64: base64,
      worksheetName: worksheetName || undefined,
      perfil_banco: importConfig.perfil_banco || 'auto',
      mapeamento: {
        data: importConfig.data || undefined,
        descricao: importConfig.descricao || undefined,
        detalhe_historico: importConfig.detalhe_historico || undefined,
        valor: importConfig.valor || undefined,
        tipo: importConfig.tipo || undefined
      },
      defaults: {
        conta_bancaria_id: importConfig.conta_bancaria_id || null,
        conta_destino_id: importConfig.conta_destino_id || null,
        centro_custo_id: importConfig.centro_custo_id || null,
        status: importConfig.status || 'confirmado'
      }
    });

    return response.data || {};
  };

  const carregarLotesImportacao = async () => {
    try {
      const response = await axios.get('/api/importacoes-extrato');
      setImportLotes(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erro ao carregar lotes de importação:', error);
    }
  };

  const excluirLoteImportacao = async (loteId) => {
    if (!window.confirm('Deseja excluir este lote? Todos os lançamentos importados nele serão removidos.')) return;
    try {
      await axios.delete(`/api/importacoes-extrato/${loteId}`);
      toast.success('Lote excluído com sucesso');
      await Promise.all([carregarLotesImportacao(), carregarDados()]);
    } catch (error) {
      console.error('Erro ao excluir lote de importação:', error);
      toast.error(error.response?.data?.error || 'Erro ao excluir lote');
    }
  };

  const handleGerarPreviewImportacao = async () => {
    try {
      if (!importFile) {
        toast.error('Selecione um arquivo .xlsx');
        return;
      }

      setImportandoArquivo(true);
      const base64 = await fileToBase64(importFile);
      const sameFile = base64 === importFileBase64;
      const previousRows = sameFile ? importRows : [];
      setImportFileBase64(base64);
      const payload = await requestImportPreview(base64, sameFile ? importPreview?.worksheetName : undefined);
      applyImportPreviewPayload(payload, {
        previousRows,
        syncDetected: true,
      });
    } catch (error) {
      console.error('Erro ao gerar preview de importacao:', error);
      toast.error(error.response?.data?.error || 'Erro ao ler arquivo XLSX');
    } finally {
      setImportandoArquivo(false);
    }
  };

  const handleConfirmarImportacao = async () => {
    try {
      if (!importFileBase64) {
        toast.error('Faça a leitura do arquivo antes de importar');
        return;
      }
      if (!importConfig.data || !importConfig.descricao || !importConfig.valor) {
        toast.error('Mapeie as colunas de data, descrição e valor');
        return;
      }
      if (!importRows.length) {
        toast.error('Gere a prévia com as linhas do extrato');
        return;
      }

      const criticas = importRows.filter((r) => isCriticalImportRow(r));
      if (criticas.length > 0) {
        toast.error(`Existem ${criticas.length} linhas com inconsistência crítica. Corrija ou ignore antes de confirmar.`);
        return;
      }

      const rowsToImport = importRows
        .map((row) => ({
          row_number: row.row_number,
          tipo: row.tipo || null,
          plano_conta_id: row.plano_conta_id || null,
          centro_custo_id: row.tipo === 'patrimonial' ? null : (row.centro_custo_id || null),
          conta_bancaria_id: row.conta_bancaria_id || null,
          conta_destino_id: row.conta_destino_id || null,
          direcao_caixa: row.tipo === 'patrimonial' ? (row.direcao_caixa || null) : null,
          status: row.status || importConfig.status || 'confirmado',
          skip: row.skip === true || row.valido === false || (row.tipo !== 'transferencia' && (!row.plano_conta_id || (row.tipo === 'patrimonial' && !row.direcao_caixa)))
        }));

      if (!rowsToImport.some((r) => !r.skip)) {
        toast.error('Selecione ao menos 1 linha válida para importar');
        return;
      }

      setImportandoConfirmacao(true);
      const payload = {
        fileBase64: importFileBase64,
        fileName: importFile?.name || 'extrato.xlsx',
        worksheetName: importPreview?.worksheetName || undefined,
        perfil_banco: importConfig.perfil_banco || 'auto',
        mapeamento: {
          data: importConfig.data,
          descricao: importConfig.descricao,
          detalhe_historico: importConfig.detalhe_historico || undefined,
          valor: importConfig.valor,
          tipo: importConfig.tipo || undefined
        },
        defaults: {
          conta_bancaria_id: importConfig.conta_bancaria_id || null,
          conta_destino_id: importConfig.conta_destino_id || null,
          centro_custo_id: importConfig.centro_custo_id || null,
          status: importConfig.status || 'confirmado'
        },
        rows: rowsToImport
      };

      let response;
      try {
        response = await axios.post('/api/lancamentos/importar-xlsx', payload);
      } catch (error) {
        if (error.response?.status === 409) {
          const confirmar = window.confirm('Este extrato já foi importado. Deseja importar novamente mesmo assim?');
          if (!confirmar) {
            toast('Importação cancelada para evitar duplicidade.');
            return;
          }
          response = await axios.post('/api/lancamentos/importar-xlsx', { ...payload, force_reimport: true });
        } else {
          throw error;
        }
      }

      const data = response.data || {};
      toast.success(`Importação concluída: ${toNumber(data.linhas_importadas)} lançamentos`);
      if (toNumber(data.linhas_com_erro) > 0) {
        toast.error(`Linhas com erro: ${data.linhas_com_erro}`);
      }

      handleFecharImportacao();
    } catch (error) {
      console.error('Erro ao importar extrato:', error);
      toast.error(error.response?.data?.error || 'Erro ao importar extrato');
    } finally {
      setImportandoConfirmacao(false);
    }
  };

  const handleChangeImportRow = (rowNumber, field, value) => {
    setImportRows((prev) => {
      const shouldResetPersistedMapping = [
        'plano_conta_id',
        'tipo',
        'centro_custo_id',
        'conta_bancaria_id',
        'conta_destino_id',
        'direcao_caixa'
      ].includes(field);

      return prev.map((row) => (
        row.row_number === rowNumber
          ? recomputeImportRow({
            ...row,
            [field]: value,
            ...(field === 'tipo' ? {
              plano_conta_id: '',
              centro_custo_id: value === 'patrimonial' ? '' : row.centro_custo_id,
              direcao_caixa: value === 'patrimonial' ? (row.direcao_caixa || 'saida') : '',
            } : {}),
            persist_mapping: shouldResetPersistedMapping ? false : row.persist_mapping,
            manual_override_fields: {
              ...(row.manual_override_fields || {}),
              [field]: true,
            },
          })
          : row
      ));
    });
  };

  const handleChangeImportMappingExpression = (rowNumber, value) => {
    setImportRows((prev) => prev.map((row) => {
      if (row.row_number !== rowNumber) return row;

      const expressionEdited = normalizeImportText(value) !== normalizeImportText(row.detalhe_historico_text);
      return recomputeImportRow({
        ...row,
        expressao_mapeamento: value,
        persist_mapping: false,
        manual_override_fields: {
          ...(row.manual_override_fields || {}),
          mapeamento_expressao: expressionEdited,
        },
      });
    }));
  };

  const handleToggleImportRowSkip = (rowNumber) => {
    setImportRows((prev) => prev.map((row) => (
      row.row_number === rowNumber ? { ...row, skip: !row.skip } : row
    )));
  };

  const handleToggleImportRowMapping = async (rowNumber) => {
    const target = importRows.find((row) => row.row_number === rowNumber);
    if (!target) return;

    const mappingExpression = normalizeImportText(target.expressao_mapeamento || target.detalhe_historico_text);
    if (!mappingExpression) {
      toast.error('Informe a expressão do detalhe histórico antes de mapear.');
      return;
    }

    if (!canPersistImportMapping(target)) {
      toast.error(
        target.tipo === 'transferencia'
          ? 'Defina contas de origem e destino validas antes de mapear esta transferencia.'
          : 'Defina o tipo e o plano de contas antes de mapear esta linha.'
      );
      return;
    }

    try {
      setSavingImportMappingRows((prev) => (
        prev.includes(rowNumber) ? prev : [...prev, rowNumber]
      ));

      const mappingPayload = buildImportMappingPayload(target);
      const saveResponse = target.mapeamento_regra_id
        ? await axios.put(`/api/historico-mapeamentos/${target.mapeamento_regra_id}`, mappingPayload)
        : await axios.post('/api/historico-mapeamentos', mappingPayload);
      const savedMappingId = target.mapeamento_regra_id || saveResponse?.data?.id || null;
      const previousRows = importRows.map((row) => (
        row.row_number === rowNumber
          ? recomputeImportRow({
            ...row,
            persist_mapping: true,
            mapeado_por_historico: true,
            mapeamento_regra_id: savedMappingId,
            similaridade_mapeamento: 1,
            origem_sugestao: 'detalhe_contains',
          })
          : row
      ));

      try {
        const payload = await requestImportPreview(importFileBase64, importPreview?.worksheetName);
        applyImportPreviewPayload(payload, {
          previousRows,
          persistMappingRows: [rowNumber],
        });
        toast.success('Mapeamento salvo e sugestões atualizadas.');
      } catch (refreshError) {
        console.error('Erro ao atualizar a prévia após salvar mapeamento:', refreshError);
        setImportRows(previousRows);
        toast.success('Mapeamento salvo. Atualize a prévia para reaplicar as sugestões.');
      }
    } catch (error) {
      console.error('Erro ao salvar mapeamento da importacao:', error);
      toast.error(error.response?.data?.error || 'Erro ao salvar mapeamento');
    } finally {
      setSavingImportMappingRows((prev) => prev.filter((item) => item !== rowNumber));
    }
  };

  const totalPercentualRateio = rateiosCentro.reduce((sum, item) => sum + toNumber(item.percentual), 0);
  const totalComposicoes = composicoes.reduce((sum, item) => sum + toNumber(item.valor), 0);

  const resetRateioCentro = () => {
    setUsarRateioCentro(false);
    setRateiosCentro([{ centro_custo_id: '', percentual: 100 }]);
  };

  const resetComposicoes = (direcao = 'saida') => {
    setComposicoes(direcao === 'entrada'
      ? [
          { tipo: 'patrimonial', plano_conta_id: '', centro_custo_id: '', valor: '', descricao: 'Principal' },
          { tipo: 'receita', plano_conta_id: '', centro_custo_id: '', valor: '', descricao: 'Receita/encargos' },
        ]
      : [
          { tipo: 'patrimonial', plano_conta_id: '', centro_custo_id: '', valor: '', descricao: 'Principal' },
          { tipo: 'despesa', plano_conta_id: '', centro_custo_id: '', valor: '', descricao: 'Juros/encargos' },
        ]);
  };

  const addRateioCentro = () => {
    setRateiosCentro((prev) => [...prev, { centro_custo_id: '', percentual: 0 }]);
  };

  const removeRateioCentro = (index) => {
    setRateiosCentro((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, idx) => idx !== index);
    });
  };

  const updateRateioCentro = (index, field, value) => {
    setRateiosCentro((prev) => prev.map((item, idx) => (
      idx === index ? { ...item, [field]: value } : item
    )));
  };

  const addComposicao = () => {
    const direcao = watch('direcao_caixa') || 'saida';
    setComposicoes((prev) => [...prev, {
      tipo: direcao === 'entrada' ? 'receita' : 'despesa',
      plano_conta_id: '',
      centro_custo_id: '',
      valor: '',
      descricao: '',
    }]);
  };

  const removeComposicao = (index) => {
    setComposicoes((prev) => (prev.length <= 1 ? prev : prev.filter((_, idx) => idx !== index)));
  };

  const updateComposicao = (index, field, value) => {
    setComposicoes((prev) => prev.map((item, idx) => {
      if (idx !== index) return item;
      const next = { ...item, [field]: value };
      if (field === 'tipo') {
        next.plano_conta_id = '';
        next.centro_custo_id = value === 'patrimonial' ? '' : next.centro_custo_id;
      }
      return next;
    }));
  };

  const updateRateioCentroPorValor = (index, value) => {
    const valorRateio = toNumber(value);
    const percentual = valorLancamentoParaRateio > 0
      ? Number(((valorRateio / valorLancamentoParaRateio) * 100).toFixed(4))
      : 0;
    updateRateioCentro(index, 'percentual', percentual);
  };

  // Selecionar tipo de opera??o
  const handleSelecionarTipo = (tipo) => {
    setTipoSelecionado(tipo);
    setShowTipoModal(false);
    setManterFormularioAberto(false);
    resetRateioCentro();
    
    if (tipo === 'lancamento' || tipo === 'patrimonial' || tipo === 'composto') {
      const isComposto = tipo === 'composto';
      reset({
        data: format(new Date(), 'yyyy-MM-dd'),
        tipo: tipo === 'patrimonial' ? 'patrimonial' : (isComposto ? 'composto' : 'despesa'),
        direcao_caixa: (tipo === 'patrimonial' || isComposto) ? 'saida' : '',
        status: 'confirmado'
      });
      if (isComposto) resetComposicoes('saida');
      setShowModal(true);
    } else if (tipo === 'transferencia') {
      resetTransfer({
        data: format(new Date(), 'yyyy-MM-dd'),
        descricao: 'Transferência entre contas',
        valor: 0.01,
        tipo: 'transferencia',
        conta_bancaria_id: contasBancarias[0]?.id || '',
        conta_destino_id: contasBancarias[1]?.id || '',
        status: 'confirmado'
      });
      setShowTransferModal(true);
    }
  };

  // Submit para lan?amentos normais
  const onSubmit = async (data) => {
    try {
      const payload = { ...data };
      const isPatrimonial = payload.tipo === 'patrimonial';
      const isComposto = payload.tipo === 'composto';

      if (isComposto) {
        const linhasValidas = composicoes
          .map((item, index) => ({
            ...item,
            valor: toNumber(item.valor),
            ordem: index + 1,
          }))
          .filter((item) => item.valor > 0 || item.plano_conta_id || item.descricao);
        if (linhasValidas.length === 0) {
          toast.error('Informe ao menos uma composição');
          return;
        }
        if (linhasValidas.some((item) => !item.tipo || !item.plano_conta_id || item.valor <= 0)) {
          toast.error('Preencha tipo, plano e valor de todas as composições');
          return;
        }
        if (Math.abs(totalComposicoes - toNumber(payload.valor)) > 0.01) {
          toast.error('A soma das composições deve bater com o valor total');
          return;
        }
        payload.plano_conta_id = '';
        payload.centro_custo_id = '';
        payload.rateios_centro_custo = [];
        payload.composicoes = linhasValidas;
      } else if (isPatrimonial) {
        payload.centro_custo_id = '';
        payload.rateios_centro_custo = [];
        payload.composicoes = [];
      } else if (usarRateioCentro) {
        const invalidRow = rateiosCentro.find((item) => !item.centro_custo_id || toNumber(item.percentual) <= 0);
        if (invalidRow) {
          toast.error('Preencha todos os centros e percentuais do rateio');
          return;
        }

        const centros = rateiosCentro.map((item) => String(item.centro_custo_id));
        if (new Set(centros).size !== centros.length) {
          toast.error('Não repita o mesmo centro de custo no rateio');
          return;
        }

        if (Math.abs(totalPercentualRateio - 100) > 0.01) {
          toast.error('A soma dos percentuais do rateio deve ser 100%');
          return;
        }

        payload.centro_custo_id = '';
        payload.rateios_centro_custo = rateiosCentro.map((item) => ({
          centro_custo_id: item.centro_custo_id,
          percentual: toNumber(item.percentual),
        }));
        payload.composicoes = [];
      } else {
        payload.rateios_centro_custo = [];
        payload.composicoes = [];
      }

      if (editingId) {
        await axios.put(`/api/lancamentos/${editingId}`, payload);
        toast.success('Lançamento atualizado com sucesso!');
      } else {
        await axios.post('/api/lancamentos', payload);
        toast.success('Lançamento cadastrado com sucesso!');
      }

      if (!editingId && manterFormularioAberto) {
        reset({
          data: data.data || format(new Date(), 'yyyy-MM-dd'),
          descricao: '',
          valor: '',
          tipo: data.tipo || 'despesa',
          direcao_caixa: (data.tipo === 'patrimonial' || data.tipo === 'composto') ? (data.direcao_caixa || 'saida') : '',
          plano_conta_id: data.plano_conta_id || '',
          conta_bancaria_id: data.conta_bancaria_id || '',
          centro_custo_id: (data.tipo === 'patrimonial' || data.tipo === 'composto') ? '' : (data.centro_custo_id || ''),
          status: data.status || 'confirmado'
        });
        if (data.tipo === 'composto') resetComposicoes(data.direcao_caixa || 'saida');
        if (!usarRateioCentro) {
          resetRateioCentro();
        }
      } else {
        setShowModal(false);
        reset();
        resetRateioCentro();
      }

      setEditingId(null);
      carregarDados();
    } catch (error) {
      console.error('Erro ao salvar lan?amento:', error);
      toast.error(error.response?.data?.message || 'Erro ao salvar lançamento');
    }
  };

  // Submit para transfer?ncias
  const onSubmitTransfer = async (data) => {
    try {
      // Valida??o adicional no frontend
      if (data.conta_bancaria_id === data.conta_destino_id) {
        toast.error('Conta de origem e destino não podem ser iguais!');
        return;
      }

      if (data.valor <= 0) {
        toast.error('Valor da transferência deve ser maior que zero!');
        return;
      }

      let response;
      if (editingId) {
        // PARA EDICAO: usar PUT
        response = await axios.put(`/api/lancamentos/${editingId}`, data);
        toast.success('Transferência atualizada com sucesso!');
      } else {
        // PARA NOVA: Usar POST
        response = await axios.post('/api/lancamentos', data);
        toast.success('Transferência realizada com sucesso!');
      }
      
      // Verificar se a resposta foi bem sucedida
      if (response.data.success) {
        if (!editingId && manterFormularioAberto) {
          resetTransfer({
            data: data.data || format(new Date(), 'yyyy-MM-dd'),
            descricao: 'Transferência entre contas',
            valor: 0.01,
            tipo: 'transferencia',
            conta_bancaria_id: data.conta_bancaria_id || '',
            conta_destino_id: data.conta_destino_id || '',
            status: data.status || 'confirmado'
          });
        } else {
          setShowTransferModal(false);
          resetTransfer();
        }

        setEditingId(null);
        carregarDados();
      } else {
        toast.error(response.data.message || 'Erro ao salvar transferência');
      }
      
    } catch (error) {
      console.error('Erro ao realizar transfer?ncia:', error);
      
      // Verificar se ? um erro de valida??o
      if (error.response?.data?.errors) {
        const errors = error.response.data.errors;
        errors.forEach(err => {
          toast.error(`${err.path}: ${err.msg}`);
        });
      } else {
        const errorMsg = error.response?.data?.message || error.message || 'Erro ao realizar transferência';
        toast.error(errorMsg);
      }
    }
  };

  const handleEdit = (lancamento) => {
    setManterFormularioAberto(false);
    // Se for transfer?ncia, editar no modal de transfer?ncia
    if (lancamento.tipo === 'transferencia') {
      resetRateioCentro();
      resetTransfer({
        ...lancamento,
        data: format(parseISO(lancamento.data), 'yyyy-MM-dd'),
        conta_bancaria_id: lancamento.conta_bancaria_id || '',
        conta_destino_id: lancamento.conta_destino_id || ''
      });
      setEditingId(lancamento.id);
      setShowTransferModal(true);
    } else {
      reset({
        ...lancamento,
        data: format(parseISO(lancamento.data), 'yyyy-MM-dd'),
        direcao_caixa: lancamento.direcao_caixa || (['patrimonial', 'composto'].includes(lancamento.tipo) ? 'saida' : '')
      });
      const rateios = Array.isArray(lancamento.rateios_centro_custo) ? lancamento.rateios_centro_custo : [];
      if (lancamento.tipo === 'composto') {
        resetRateioCentro();
        const existingComposicoes = Array.isArray(lancamento.composicoes) ? lancamento.composicoes : [];
        setComposicoes(existingComposicoes.length > 0
          ? existingComposicoes.map((item) => ({
              tipo: item.tipo || 'despesa',
              plano_conta_id: item.plano_conta_id || '',
              centro_custo_id: item.centro_custo_id || '',
              valor: item.valor || '',
              descricao: item.descricao || '',
            }))
          : []);
      } else if (lancamento.tipo === 'patrimonial') {
        resetRateioCentro();
        resetComposicoes('saida');
      } else if (rateios.length > 0) {
        setUsarRateioCentro(true);
        setRateiosCentro(rateios.map((item) => ({
          centro_custo_id: item.centro_custo_id || '',
          percentual: toNumber(item.percentual),
        })));
      } else {
        resetRateioCentro();
        resetComposicoes('saida');
      }
      setEditingId(lancamento.id);
      setShowModal(true);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este lançamento?')) {
      return;
    }

    try {
      await axios.delete(`/api/lancamentos/${id}`);
      toast.success('Lançamento excluído com sucesso!');
      carregarDados();
    } catch (error) {
      console.error('Erro ao excluir lan?amento:', error);
      toast.error('Erro ao excluir lançamento');
    }
  };

  const limparFiltros = () => {
    setFiltros({
      data_inicio: '',
      data_fim: '',
      descricao: '',
      tipo: 'todos',
      plano_conta_id: 'todos',
      centro_custo_id: 'todos',
      conta_bancaria_id: 'todas'
    });
  };

  const totalReceitas = lancamentos
    .reduce((sum, l) => {
      if (l.tipo === 'receita') return sum + toNumber(l.valor);
      if (l.tipo === 'composto') {
        return sum + (Array.isArray(l.composicoes) ? l.composicoes : [])
          .filter((item) => item.tipo === 'receita')
          .reduce((acc, item) => acc + toNumber(item.valor), 0);
      }
      return sum;
    }, 0);

  const totalDespesas = lancamentos
    .reduce((sum, l) => {
      if (l.tipo === 'despesa') return sum + toNumber(l.valor);
      if (l.tipo === 'composto') {
        return sum + (Array.isArray(l.composicoes) ? l.composicoes : [])
          .filter((item) => item.tipo === 'despesa')
          .reduce((acc, item) => acc + toNumber(item.valor), 0);
      }
      return sum;
    }, 0);

  const totalTransferencias = lancamentos
    .filter(l => l.tipo === 'transferencia')
    .reduce((sum, l) => sum + toNumber(l.valor), 0);

  const totalPatrimonialEntradas = lancamentos
    .filter(l => l.tipo === 'patrimonial' && l.direcao_caixa === 'entrada')
    .reduce((sum, l) => sum + toNumber(l.valor), 0);

  const totalPatrimonialSaidas = lancamentos
    .filter(l => l.tipo === 'patrimonial' && l.direcao_caixa === 'saida')
    .reduce((sum, l) => sum + toNumber(l.valor), 0);

  const saldo = totalReceitas - totalDespesas;

  // Filtrar contas para dropdown (excluir a selecionada)
  const contasParaDestino = contasBancarias.filter(conta => conta.id.toString() !== contaOrigem);
  const contasParaOrigem = contasBancarias.filter(conta => conta.id.toString() !== contaDestino);

  return (
    <div className="animate-fade-in">
      {!isImportRoute && (
      <>
      <div className="mb-6 flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Lançamentos Financeiros</h1>
          <p className="text-gray-600">Gerencie todas as movimentações financeiras</p>
        </div>
        <div className="flex flex-col items-stretch gap-3 xl:items-end">
          <button
            onClick={handleNovoLancamento}
            className="btn-primary self-start xl:self-end"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Nova Operação
          </button>

          <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 shadow-sm">
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => setMostrarFiltros(!mostrarFiltros)}
                className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm transition hover:border-gray-300 hover:bg-gray-50"
              >
                <FunnelIcon className="h-4 w-4 mr-1.5" />
                Filtros
              </button>
              <button
                onClick={handleAbrirEdicaoLote}
                className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm transition hover:border-gray-300 hover:bg-gray-50"
              >
                Edicao em Lote
              </button>
              <button
                onClick={handleAbrirConferenciaSaldo}
                className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm transition hover:border-gray-300 hover:bg-gray-50"
              >
                <TableCellsIcon className="h-4 w-4 mr-1.5" />
                Conferencia de Saldo
              </button>
              <button
                onClick={handleAbrirImportacao}
                className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm transition hover:border-gray-300 hover:bg-gray-50"
              >
                Importar XLSX
              </button>
              <button
                onClick={handleExportarContabil}
                disabled={exportandoContabil}
                className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm transition hover:border-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <TableCellsIcon className="h-4 w-4 mr-1.5" />
                {exportandoContabil ? 'Gerando...' : 'Exportacao Contabil'}
              </button>
              <ExportButtons tipo="lancamentos" filtros={filtros} compact />
            </div>
          </div>
        </div>
      </div>

      {/* Cards de Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="card bg-success-50 border-success-200">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-success-100">
                <CurrencyDollarIcon className="h-6 w-6 text-success-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-success-600">Total Receitas</p>
                <p className="text-2xl font-bold text-gray-900">
                  R$ {totalReceitas.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card bg-danger-50 border-danger-200">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-danger-100">
                <CurrencyDollarIcon className="h-6 w-6 text-danger-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-danger-600">Total Despesas</p>
                <p className="text-2xl font-bold text-gray-900">
                  R$ {totalDespesas.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card bg-blue-50 border-blue-200">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-100">
                <ArrowsRightLeftIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-blue-600">Total Transferências</p>
                <p className="text-2xl font-bold text-gray-900">
                  R$ {totalTransferencias.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card bg-primary-50 border-primary-200">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-primary-100">
                <DocumentTextIcon className="h-6 w-6 text-primary-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-primary-600">Saldo do Período</p>
                <p className={`text-2xl font-bold ${saldo >= 0 ? 'text-success-600' : 'text-danger-600'}`}>
                  {saldo >= 0 ? '+' : ''}R$ {saldo.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Patrimonial: +R$ {totalPatrimonialEntradas.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} / -R$ {totalPatrimonialSaidas.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filtros */}
      {mostrarFiltros && (
        <div className="card mb-6 animate-slide-in">
          <div className="card-body p-4">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-base font-semibold">Filtros Avançados</h3>
              <button
                onClick={() => setMostrarFiltros(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>

            <DateQuickPresets
              mode="range"
              startDate={filtros.data_inicio}
              endDate={filtros.data_fim}
              onSelect={({ startDate, endDate }) =>
                setFiltros((old) => ({ ...old, data_inicio: startDate, data_fim: endDate }))
              }
              className="mb-3"
            />
            
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
              <div className="form-group mb-0">
                <label className="form-label text-xs mb-1">
                  <CalendarIcon className="h-3.5 w-3.5 inline mr-1" />
                  Data Início
                </label>
                <input
                  type="date"
                  value={filtros.data_inicio}
                  onChange={(e) => setFiltros({ ...filtros, data_inicio: e.target.value })}
                  className="form-input text-sm py-1.5"
                />
              </div>
              
              <div className="form-group mb-0">
                <label className="form-label text-xs mb-1">
                  <CalendarIcon className="h-3.5 w-3.5 inline mr-1" />
                  Data Fim
                </label>
                <input
                  type="date"
                  value={filtros.data_fim}
                  onChange={(e) => setFiltros({ ...filtros, data_fim: e.target.value })}
                  className="form-input text-sm py-1.5"
                />
              </div>
              
              <div className="form-group mb-0">
                <label className="form-label text-xs mb-1">Descrição</label>
                <input
                  type="text"
                  value={filtros.descricao}
                  onChange={(e) => setFiltros({ ...filtros, descricao: e.target.value })}
                  className="form-input text-sm py-1.5"
                  placeholder="Buscar por descrição..."
                />
              </div>
              
              <div className="form-group mb-0">
                <label className="form-label text-xs mb-1">Tipo</label>
                <select
                  value={filtros.tipo}
                  onChange={(e) => setFiltros({ ...filtros, tipo: e.target.value })}
                  className="form-select text-sm py-1.5"
                >
                  <option value="todos">Todos</option>
                  <option value="receita">Receitas</option>
                  <option value="despesa">Despesas</option>
                  <option value="transferencia">Transferências</option>
                  <option value="patrimonial">Patrimoniais</option>
                  <option value="composto">Compostos</option>
                </select>
              </div>

              <div className="form-group mb-0">
                <label className="form-label text-xs mb-1">Plano de Contas</label>
                <select
                  value={filtros.plano_conta_id}
                  onChange={(e) => setFiltros({ ...filtros, plano_conta_id: e.target.value })}
                  className="form-select text-sm py-1.5"
                >
                  <option value="todos">Todos</option>
                  {planosContas.map(plano => (
                    <option key={plano.id} value={plano.id}>
                      {plano.codigo} - {plano.descricao}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group mb-0">
                <label className="form-label text-xs mb-1">Centro de Custo</label>
                <select
                  value={filtros.centro_custo_id}
                  onChange={(e) => setFiltros({ ...filtros, centro_custo_id: e.target.value })}
                  className="form-select text-sm py-1.5"
                >
                  <option value="todos">Todos</option>
                  {centrosCustos.map(centro => (
                    <option key={centro.id} value={centro.id}>
                      {centro.codigo} - {centro.descricao}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group mb-0">
                <label className="form-label text-xs mb-1">Conta Bancária</label>
                <select
                  value={filtros.conta_bancaria_id}
                  onChange={(e) => setFiltros({ ...filtros, conta_bancaria_id: e.target.value })}
                  className="form-select text-sm py-1.5"
                >
                  <option value="todas">Todas</option>
                  {contasBancarias.map(conta => (
                    <option key={conta.id} value={conta.id}>
                      {conta.banco} - {conta.conta}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="mt-3 flex flex-wrap gap-2 justify-between">
              <button
                onClick={limparFiltros}
                className="btn-secondary text-sm py-1.5 px-3"
              >
                Limpar Filtros
              </button>
              <div className="flex gap-2">
                <button
                  onClick={() => setMostrarFiltros(false)}
                  className="btn-secondary text-sm py-1.5 px-3"
                >
                  Fechar
                </button>
                <button
                  onClick={carregarDados}
                  className="btn-primary text-sm py-1.5 px-3"
                >
                  <MagnifyingGlassIcon className="h-4 w-4 mr-2" />
                  Aplicar Filtros
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabela de Lançamentos */}
      <div className="card">
        <div className="card-header flex justify-between items-center">
          <h3 className="text-lg font-semibold">
            Lista de Operações ({lancamentos.length})
          </h3>
          {carregando && (
            <div className="loader h-6 w-6"></div>
          )}
        </div>
        
        <div className="card-body p-0">
          {lancamentos.length > 0 ? (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Tipo</th>
                    <th>Data</th>
                    <th>Descrição</th>
                    <th>Detalhes</th>
                    <th>Valor</th>
                    <th>Status</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {lancamentos.map((lancamento) => (
                    <tr key={lancamento.id}>
                      <td>
                        <div className="flex items-center">
                          {lancamento.tipo === 'transferencia' ? (
                            <>
                              <ArrowsRightLeftIcon className="h-4 w-4 text-blue-600 mr-1" />
                              <span className="badge badge-blue">Transferência</span>
                            </>
                          ) : lancamento.tipo === 'receita' ? (
                            <>
                              <ArrowRightCircleIcon className="h-4 w-4 text-success-600 mr-1" />
                              <span className="badge badge-success">Receita</span>
                            </>
                          ) : lancamento.tipo === 'patrimonial' ? (
                            <>
                              <BuildingLibraryIcon className="h-4 w-4 text-amber-700 mr-1" />
                              <span className="badge badge-warning">Patrimonial</span>
                            </>
                          ) : lancamento.tipo === 'composto' ? (
                            <>
                              <DocumentTextIcon className="h-4 w-4 text-indigo-700 mr-1" />
                              <span className="badge badge-blue">Composto</span>
                            </>
                          ) : (
                            <>
                              <ArrowRightCircleIcon className="h-4 w-4 text-danger-600 mr-1 rotate-180" />
                              <span className="badge badge-danger">Despesa</span>
                            </>
                          )}
                        </div>
                      </td>
                      <td>{format(parseISO(lancamento.data), 'dd/MM/yyyy')}</td>
                      <td>
                        <div className="max-w-xs">
                          <p className="font-medium text-gray-900">{lancamento.descricao}</p>
                          <p className="text-xs text-gray-500">ID: {lancamento.id}</p>
                        </div>
                      </td>
                      <td>
                        {lancamento.tipo === 'transferencia' ? (
                          <div className="text-sm">
                            <p className="font-medium text-gray-900">Transferência entre contas</p>
                            <p className="text-xs text-gray-500">
                              {lancamento.conta_bancaria_banco} → {lancamento.conta_destino_id ? 'Conta ' + lancamento.conta_destino_id : 'N/D'}
                            </p>
                          </div>
                        ) : lancamento.tipo === 'patrimonial' ? (
                          <div className="text-sm">
                            <p className="font-medium text-gray-900">
                              {lancamento.plano_conta_descricao || 'Sem conta patrimonial'}
                            </p>
                            <p className="text-xs text-gray-500">
                              {lancamento.direcao_caixa === 'entrada' ? 'Entrada no caixa' : 'Saida do caixa'} • {lancamento.conta_bancaria_banco || 'Sem conta'}
                            </p>
                          </div>
                        ) : lancamento.tipo === 'composto' ? (
                          <div className="text-sm">
                            <p className="font-medium text-gray-900">
                              {(Array.isArray(lancamento.composicoes) ? lancamento.composicoes : []).length} composição(ões)
                            </p>
                            <div className="mt-1 space-y-0.5 text-xs text-gray-500">
                              {(Array.isArray(lancamento.composicoes) ? lancamento.composicoes : []).slice(0, 3).map((item, index) => (
                                <p key={item.id || index}>
                                  {item.plano_conta_codigo ? `${item.plano_conta_codigo} - ` : ''}{item.plano_conta_descricao || item.tipo}: R$ {toNumber(item.valor).toFixed(2)}
                                </p>
                              ))}
                              {(Array.isArray(lancamento.composicoes) ? lancamento.composicoes : []).length > 3 && (
                                <p>+ {(lancamento.composicoes.length - 3)} linha(s)</p>
                              )}
                              <p>{lancamento.direcao_caixa === 'entrada' ? 'Entrada no caixa' : 'Saida do caixa'} • {lancamento.conta_bancaria_banco || 'Sem conta'}</p>
                            </div>
                          </div>
                        ) : (
                          <div className="text-sm">
                            <p className="font-medium text-gray-900">
                              {lancamento.plano_conta_descricao || 'Sem plano de conta'}
                            </p>
                            <p className="text-xs text-gray-500">
                              {Array.isArray(lancamento.rateios_centro_custo) && lancamento.rateios_centro_custo.length > 0
                                ? `Rateado em ${lancamento.rateios_centro_custo.length} centro(s)`
                                : (lancamento.centro_custo_descricao || 'Sem centro de custo')
                              } • {lancamento.conta_bancaria_banco || 'Sem conta'}
                            </p>
                          </div>
                        )}
                      </td>
                      <td>
                        <div className={`font-semibold ${
                          lancamento.tipo === 'receita' ? 'text-success-600' :
                          lancamento.tipo === 'transferencia' ? 'text-blue-600' :
                          lancamento.tipo === 'patrimonial' ? 'text-amber-700' :
                          lancamento.tipo === 'composto' ? 'text-indigo-700' : 'text-danger-600'
                        }`}>
                          {lancamento.tipo === 'receita' ? '+' : 
                           lancamento.tipo === 'transferencia' ? '↔' :
                           ['patrimonial', 'composto'].includes(lancamento.tipo) ? (lancamento.direcao_caixa === 'entrada' ? '+' : '-') : '-'}
                          R$ {toNumber(lancamento.valor).toFixed(2)}
                        </div>
                      </td>
                      <td>
                        <span className={`badge ${
                          lancamento.status === 'confirmado' ? 'badge-success' :
                          lancamento.status === 'pendente' ? 'badge-warning' :
                          'badge-danger'
                        }`}>
                          {lancamento.status === 'confirmado' ? 'Confirmado' :
                           lancamento.status === 'pendente' ? 'Pendente' : 'Cancelado'}
                        </span>
                      </td>
                      <td>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEdit(lancamento)}
                            className="text-primary-600 hover:text-primary-900 p-1"
                            title="Editar"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(lancamento.id)}
                            className="text-danger-600 hover:text-danger-900 p-1"
                            title="Excluir"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-8 text-center text-gray-500">
              <BanknotesIcon className="h-12 w-12 mx-auto text-gray-300 mb-3" />
              <p className="text-lg font-medium">Nenhuma operação encontrada</p>
              <p className="text-sm mt-1">Use os filtros para buscar ou cadastre uma nova operação</p>
              <button
                onClick={handleNovoLancamento}
                className="btn-primary mt-4"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Cadastrar Primeira Operação
              </button>
            </div>
          )}
        </div>
      </div>
      </>
      )}

      {/* PÁGINA: Importação de Extrato XLSX */}
      {isImportRoute && (
        <div className="space-y-6">
          <div className="mx-auto w-full max-w-[1920px] rounded-2xl border border-gray-200 bg-white shadow-xl">
            <div className="flex items-center justify-between gap-4 border-b bg-white px-4 py-4 sm:px-6">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => handleFecharImportacao({ reset: false })}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 transition hover:border-gray-300 hover:text-gray-700"
                  title="Voltar para lançamentos"
                >
                  <ArrowLeftIcon className="h-5 w-5" />
                </button>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.24em] text-primary-700">Importação assistida</p>
                  <h3 className="mt-1 text-xl font-semibold">Importar Extrato Bancário (XLSX)</h3>
                  <p className="text-sm text-gray-600">Mapeie as colunas, revise as linhas e confirme a importação.</p>
                </div>
              </div>
              <button
                onClick={() => handleFecharImportacao()}
                className="text-gray-400 hover:text-gray-600"
                type="button"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="bg-gray-50 p-4 sm:p-6 space-y-6">
              <div className="grid grid-cols-1 gap-4 xl:grid-cols-[360px_minmax(0,1fr)]">
                <div className="card border-gray-200 shadow-sm">
                  <div className="card-body p-4 space-y-4">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">1. Arquivo e perfil</p>
                      <p className="mt-1 text-xs text-gray-500">Selecione o extrato e gere a prévia para iniciar a conferência.</p>
                    </div>
                    <div>
                      <label className="form-label">Arquivo XLSX</label>
                      <input
                        type="file"
                        accept=".xlsx"
                        className="form-input"
                        onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                      />
                      <p className="mt-2 text-xs text-gray-500">
                        {importFile?.name ? `Selecionado: ${importFile.name}` : 'Nenhum arquivo selecionado ainda.'}
                      </p>
                    </div>
                    <div>
                      <label className="form-label">Perfil do Extrato</label>
                      <select
                        className="form-select"
                        value={importConfig.perfil_banco}
                        onChange={(e) => setImportConfig({ ...importConfig, perfil_banco: e.target.value })}
                      >
                        {importProfileOptions.map((perfil) => (
                          <option key={perfil.id} value={perfil.id}>{perfil.nome}</option>
                        ))}
                      </select>
                      <p className="mt-2 text-xs text-gray-500">
                        {selectedImportProfile?.descricao || 'Perfil sem descrição cadastrada.'}
                      </p>
                    </div>
                    <button
                      onClick={handleGerarPreviewImportacao}
                      className="btn-primary w-full"
                      disabled={importandoArquivo}
                    >
                      {importandoArquivo ? 'Lendo arquivo...' : 'Ler arquivo e montar prévia'}
                    </button>
                  </div>
                </div>

                <div className="card border-gray-200 shadow-sm">
                  <div className="card-body p-4">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                      <div>
                        <p className="text-sm font-semibold text-gray-900">Área de revisão</p>
                        <p className="mt-1 text-sm text-gray-600">
                          A prévia agora usa melhor a largura disponível: filtros rápidos no topo e conferência focada nas linhas abaixo.
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {importFile?.name && (
                          <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
                            {importFile.name}
                          </span>
                        )}
                        {importPreview?.worksheetName && (
                          <span className="inline-flex items-center rounded-full bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700">
                            Planilha: {importPreview.worksheetName}
                          </span>
                        )}
                        {Array.isArray(importPreview?.headers) && importPreview.headers.length > 0 && (
                          <span className="inline-flex items-center rounded-full bg-success-50 px-3 py-1 text-xs font-medium text-success-700">
                            {importPreview.headers.length} coluna(s)
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                      <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Passo 1</p>
                        <p className="mt-1 text-sm font-medium text-gray-900">Gerar prévia</p>
                        <p className="mt-1 text-xs text-gray-500">Valide cabeçalhos e dados antes de importar.</p>
                      </div>
                      <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Passo 2</p>
                        <p className="mt-1 text-sm font-medium text-gray-900">Revisar pendências</p>
                        <p className="mt-1 text-xs text-gray-500">Use filtros para focar apenas no que exige ação.</p>
                      </div>
                      <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Passo 3</p>
                        <p className="mt-1 text-sm font-medium text-gray-900">Salvar mapeamentos</p>
                        <p className="mt-1 text-xs text-gray-500">Persistir regras reduz esforço nas próximas importações.</p>
                      </div>
                      <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Passo 4</p>
                        <p className="mt-1 text-sm font-medium text-gray-900">Confirmar lote</p>
                        <p className="mt-1 text-xs text-gray-500">Importe apenas quando o resumo estiver limpo.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {importPreview && (
                <>
                  <div className="grid gap-3 md:grid-cols-2 2xl:grid-cols-5">
                    {importOverviewCards.map((card) => {
                      const Icon = card.icon;
                      return (
                        <div key={card.id} className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{card.label}</p>
                              <p className="mt-2 text-3xl font-semibold text-gray-900">{card.value}</p>
                              <p className="mt-1 text-xs text-gray-500">{card.helper}</p>
                            </div>
                            <span className={`flex h-10 w-10 items-center justify-center rounded-2xl ${card.iconClassName}`}>
                              <Icon className="h-5 w-5" />
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-600 shadow-sm">
                    Perfil aplicado: <span className="font-medium">{importPreview.perfil_nome || importPreview.perfil_banco || 'Nao identificado'}</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                    <div className="form-group">
                      <label className="form-label">Coluna Data *</label>
                      <select
                        className="form-select"
                        value={importConfig.data}
                        onChange={(e) => setImportConfig({ ...importConfig, data: e.target.value })}
                      >
                        <option value="">Selecione</option>
                        {importPreview.headers.map((h) => (
                          <option key={`data-${h}`} value={h}>{h}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Coluna Historico *</label>
                      <select
                        className="form-select"
                        value={importConfig.descricao}
                        onChange={(e) => setImportConfig({ ...importConfig, descricao: e.target.value })}
                      >
                        <option value="">Selecione</option>
                        {importPreview.headers.map((h) => (
                          <option key={`desc-${h}`} value={h}>{h}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Coluna Valor *</label>
                      <select
                        className="form-select"
                        value={importConfig.valor}
                        onChange={(e) => setImportConfig({ ...importConfig, valor: e.target.value })}
                      >
                        <option value="">Selecione</option>
                        {importPreview.headers.map((h) => (
                          <option key={`val-${h}`} value={h}>{h}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Detalhe Historico</label>
                      <select
                        className="form-select"
                        value={importConfig.detalhe_historico}
                        onChange={(e) => setImportConfig({ ...importConfig, detalhe_historico: e.target.value })}
                      >
                        <option value="">Sem concatenar</option>
                        {importPreview.headers.map((h) => (
                          <option key={`det-${h}`} value={h}>{h}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Coluna Tipo (opcional)</label>
                      <select
                        className="form-select"
                        value={importConfig.tipo}
                        onChange={(e) => setImportConfig({ ...importConfig, tipo: e.target.value })}
                      >
                        <option value="">Inferir por sinal (ou C/D)</option>
                        {importPreview.headers.map((h) => (
                          <option key={`tipo-${h}`} value={h}>{h}</option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">Banco do Brasil: use a coluna Inf. (C=receita, D=despesa)</p>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                    <div className="mb-4 flex flex-col gap-1">
                      <p className="text-sm font-semibold text-gray-900">Configurações padrão da importação</p>
                      <p className="text-xs text-gray-500">Esses valores são aplicados antes da revisão linha a linha.</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                    <div className="form-group">
                      <label className="form-label">Conta Bancaria Padrao</label>
                      <select
                        className="form-select"
                        value={importConfig.conta_bancaria_id}
                        onChange={(e) => setImportConfig({ ...importConfig, conta_bancaria_id: e.target.value })}
                      >
                        <option value="">Sem conta</option>
                        {contasBancarias.map((conta) => (
                          <option key={conta.id} value={conta.id}>{conta.banco} - {conta.conta}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Conta Destino Padrão (Transferência)</label>
                      <select
                        className="form-select"
                        value={importConfig.conta_destino_id}
                        onChange={(e) => setImportConfig({ ...importConfig, conta_destino_id: e.target.value })}
                      >
                        <option value="">Sem conta</option>
                        {contasBancarias.map((conta) => (
                          <option key={`dest-${conta.id}`} value={conta.id}>{conta.banco} - {conta.conta}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Centro de Custo</label>
                      <select
                        className="form-select"
                        value={importConfig.centro_custo_id}
                        onChange={(e) => setImportConfig({ ...importConfig, centro_custo_id: e.target.value })}
                      >
                        <option value="">Sem centro</option>
                        {centrosCustos.map((centro) => (
                          <option key={centro.id} value={centro.id}>{centro.codigo} - {centro.descricao}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Status</label>
                      <select
                        className="form-select"
                        value={importConfig.status}
                        onChange={(e) => setImportConfig({ ...importConfig, status: e.target.value })}
                      >
                        <option value="confirmado">Confirmado</option>
                        <option value="pendente">Pendente</option>
                      </select>
                    </div>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                    <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                      <div>
                        <p className="text-sm font-semibold text-gray-900">Conciliacao por Linha</p>
                        <p className="mt-1 text-xs text-gray-500">
                          Total: {importSummary.total} | Prontas: {importSummary.prontas} | Pendentes: {importSummary.pendentes} | Erros: {importSummary.erros} | Ignoradas: {importSummary.ignoradas} | Transferências: {importSummary.transferencias} | Patrimoniais: {importSummary.patrimoniais}
                        </p>
                      </div>
                      <div className="flex flex-col gap-3 xl:items-end">
                        <div className="flex flex-wrap gap-2">
                          {importFilterOptions.map((filter) => (
                            <button
                              key={filter.id}
                              type="button"
                              onClick={() => setImportStatusFilter(filter.id)}
                              className={`rounded-full border px-3 py-2 text-sm font-medium transition ${getImportFilterButtonClassName(filter.id)}`}
                            >
                              {filter.label} ({filter.count})
                            </button>
                          ))}
                        </div>
                        <div className="flex w-full flex-col gap-3 sm:flex-row xl:w-auto">
                          <div className="relative min-w-[260px]">
                            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                            <input
                              type="text"
                              className="form-input pl-10"
                              value={importSearchTerm}
                              onChange={(e) => setImportSearchTerm(e.target.value)}
                              placeholder="Buscar por linha, histórico ou expressão"
                            />
                          </div>
                          <button onClick={handleGerarPreviewImportacao} className="btn-secondary whitespace-nowrap">Atualizar Prévia</button>
                        </div>
                      </div>
                    </div>
                    <p className="mt-3 text-sm text-gray-500">
                      Mostrando {filteredImportRows.length} de {importSummary.total} linha(s) no preview atual.
                    </p>
                    {importSummary.criticas > 0 && (
                      <div className="mt-4 mb-3 p-3 rounded border border-danger-200 bg-danger-50 text-danger-800 text-sm">
                        Existem {importSummary.criticas} linha(s) com inconsistência crítica. Corrija ou marque como ignorada para liberar a confirmação.
                      </div>
                    )}
                    {importSummary.importaveis === 0 && importSummary.total > 0 && (
                      <div className="mb-3 p-3 rounded border border-warning-200 bg-warning-50 text-warning-800 text-sm">
                        Nenhuma linha importável no momento.
                      </div>
                    )}
                    <div className="mb-3 p-3 rounded border border-primary-200 bg-primary-50 text-primary-900 text-sm">
                      A importação não cria mais mapeamentos automaticamente. Use <span className="font-semibold">Mapear</span> para salvar uma regra do tipo <span className="font-semibold">contém</span> com base na expressão do detalhe histórico.
                    </div>
                    <div className="overflow-auto rounded border border-gray-200 max-h-[56vh]">
                      <table className="table min-w-[900px]">
                        <thead>
                          <tr>
                            <th className="w-14 text-center">#</th>
                            <th className="w-28">Data</th>
                            <th>Descricao</th>
                            <th className="w-32">Valor</th>
                            <th className="w-36">Tipo</th>
                            <th className="w-[280px]">Plano de Contas</th>
                            <th className="w-28 text-center">Qualidade</th>
                            <th className="hidden 2xl:table-cell w-32 text-center">Confiança</th>
                            <th className="hidden xl:table-cell w-24 text-center">Origem</th>
                            <th className="hidden xl:table-cell w-24 text-center">Map.</th>
                            <th className="w-36">Ações</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredImportRows.map((row) => {
                            const qualityMeta = getQualidadeMeta(row);
                            const similarityMeta = getSimilarityMeta(row.similaridade_mapeamento);
                            const originMeta = getImportOriginMeta(row.origem_sugestao);
                            const mappingStatusMeta = getImportMappingStatusMeta(row);
                            return (
                            <tr key={`preview-${row.row_number}`} className={`${row.skip ? 'opacity-60' : ''} ${getImportRowCardClassName(row)}`}>
                              <td className="w-14 text-center text-xs font-semibold text-gray-600">{row.row_number}</td>
                              <td className="w-28 text-sm text-gray-700">{row.data || '-'}</td>
                              <td className="max-w-md whitespace-normal align-top">
                                <p className="font-medium text-gray-900">{row.descricao || '-'}</p>
                                {row.tipo === 'transferencia' && (
                                  <p className="mt-1 text-xs text-gray-500">
                                    {getContaBancariaLabel(row.conta_bancaria_id)} → {getContaBancariaLabel(row.conta_destino_id)}
                                  </p>
                                )}
                                <div className="mt-2">
                                  <label className="block text-[11px] font-medium text-gray-500 mb-1">
                                    Expressão para mapear
                                  </label>
                                  <input
                                    type="text"
                                    className="form-input text-xs"
                                    value={row.expressao_mapeamento || ''}
                                    onChange={(e) => handleChangeImportMappingExpression(row.row_number, e.target.value)}
                                    placeholder="Trecho do detalhe histórico"
                                  />
                                  {row.detalhe_historico_text && normalizeImportText(row.expressao_mapeamento) !== normalizeImportText(row.detalhe_historico_text) ? (
                                    <p className="text-[11px] text-gray-400 mt-1">Original: {row.detalhe_historico_text}</p>
                                  ) : null}
                                </div>
                                {Array.isArray(row.erros_validacao) && row.erros_validacao.length > 0 ? (
                                  <p className="text-xs text-danger-700 mt-1">{row.erros_validacao.join(' | ')}</p>
                                ) : null}
                                {Array.isArray(row.avisos_validacao) && row.avisos_validacao.length > 0 ? (
                                  <p className="text-xs text-warning-700 mt-1">{row.avisos_validacao.join(' | ')}</p>
                                ) : null}
                              </td>
                              <td className="font-semibold">R$ {toNumber(row.valor).toFixed(2)}</td>
                              <td>
                                <select
                                  className="form-select min-w-[128px]"
                                  value={row.tipo || ''}
                                  onChange={(e) => handleChangeImportRow(row.row_number, 'tipo', e.target.value)}
                                >
                                  <option value="receita">Receita</option>
                                  <option value="despesa">Despesa</option>
                                  <option value="transferencia">Transferência</option>
                                  <option value="patrimonial">Patrimonial</option>
                                </select>
                                {row.tipo === 'patrimonial' && (
                                  <select
                                    className="form-select min-w-[128px] mt-2"
                                    value={row.direcao_caixa || 'saida'}
                                    onChange={(e) => handleChangeImportRow(row.row_number, 'direcao_caixa', e.target.value)}
                                  >
                                    <option value="entrada">Entrada caixa</option>
                                    <option value="saida">Saida caixa</option>
                                  </select>
                                )}
                              </td>
                              <td>
                                <select
                                  className="form-select min-w-[260px]"
                                  value={row.plano_conta_id || ''}
                                  onChange={(e) => handleSelectImportPlano(row, e.target.value)}
                                  disabled={row.tipo === 'transferencia'}
                                >
                                  <option value="">{row.tipo === 'transferencia' ? 'Não se aplica' : 'Selecionar plano'}</option>
                                  {planosContas
                                    .filter((plano) => !row.tipo || plano.tipo === getPlanoTipoByLancamento(row.tipo))
                                    .map((plano) => (
                                      <option key={`${row.row_number}-${plano.id}`} value={plano.id}>
                                        {plano.codigo} - {plano.descricao}
                                      </option>
                                  ))}
                                  {row.tipo !== 'transferencia' && (
                                    <option value={CREATE_PLAN_OPTION_VALUE}>+ Criar novo plano...</option>
                                  )}
                                </select>
                              </td>
                              <td className="text-center">
                                <span className={`badge ${qualityMeta.className}`}>{qualityMeta.label}</span>
                              </td>
                              <td className="hidden 2xl:table-cell text-center">
                                <span className={`badge ${similarityMeta.className}`} title={`${similarityMeta.score}% de similaridade`}>
                                  {similarityMeta.label} ({similarityMeta.score}%)
                                </span>
                              </td>
                              <td className="hidden xl:table-cell text-center">
                                <span className="inline-flex max-w-full items-center justify-center rounded-full bg-gray-100 px-2 py-1 text-[11px] font-medium text-gray-700" title={originMeta.title}>
                                  {originMeta.label}
                                </span>
                              </td>
                              <td className="hidden xl:table-cell text-center">
                                <span className={`badge ${mappingStatusMeta.className}`} title={mappingStatusMeta.title}>
                                  {mappingStatusMeta.label}
                                </span>
                              </td>
                              <td>
                                <div className="flex flex-col gap-2 min-w-[132px]">
                                  <button
                                    className={row.persist_mapping ? 'btn-success text-xs py-1.5 px-2' : 'btn-secondary text-xs py-1.5 px-2'}
                                    onClick={() => handleToggleImportRowMapping(row.row_number)}
                                    disabled={!canPersistImportMapping(row) || savingImportMappingRows.includes(row.row_number)}
                                    title={!canPersistImportMapping(row) ? 'Defina os dados obrigatorios antes de mapear.' : 'Salvar ou atualizar esta regra de mapeamento agora.'}
                                  >
                                    {savingImportMappingRows.includes(row.row_number) ? 'Salvando...' : getImportMappingButtonLabel(row)}
                                  </button>
                                  <button
                                    className="btn-secondary text-xs py-1.5 px-2"
                                    onClick={() => handleToggleImportRowSkip(row.row_number)}
                                  >
                                    {row.skip ? 'Reincluir' : 'Ignorar'}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          )})}
                        </tbody>
                      </table>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">As alterações manuais de plano e tipo ficam apenas na linha atual. As demais linhas só são atualizadas quando um mapeamento salvo é reaplicado pelo botão Mapear.</p>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium text-gray-700">Lotes de Importação Recentes</p>
                      <button onClick={carregarLotesImportacao} className="btn-secondary">Atualizar Lotes</button>
                    </div>
                    <div className="table-container border rounded">
                      <table className="table">
                        <thead>
                          <tr>
                            <th>Arquivo</th>
                            <th>Status</th>
                            <th>Importados</th>
                            <th>Data</th>
                            <th>Ações</th>
                          </tr>
                        </thead>
                        <tbody>
                          {importLotes.map((lote) => (
                            <tr key={lote.id}>
                              <td className="max-w-xs">{lote.nome_arquivo}</td>
                              <td>{lote.status}</td>
                              <td>{toNumber(lote.linhas_importadas)}</td>
                              <td>{lote.created_at ? new Date(lote.created_at).toLocaleString('pt-BR') : '-'}</td>
                              <td>
                                <button
                                  className="text-danger-600 hover:text-danger-900"
                                  onClick={() => excluirLoteImportacao(lote.id)}
                                >
                                  Excluir lote
                                </button>
                              </td>
                            </tr>
                          ))}
                          {importLotes.length === 0 && (
                            <tr>
                              <td colSpan={5} className="text-center text-gray-500 py-4">Nenhum lote encontrado</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="flex flex-col gap-3 border-t bg-white px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
              <p className="text-sm text-gray-600">
                {importPreview
                  ? `${importSummary.importaveis} linha(s) prontas para importar e ${importSummary.criticas} crítica(s) em aberto.`
                  : 'Leia um arquivo para habilitar a confirmação da importação.'}
              </p>
              <div className="flex justify-end space-x-3">
              <button
                onClick={() => handleFecharImportacao()}
                className="btn-secondary"
                type="button"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmarImportacao}
                className="btn-primary"
                disabled={!canConfirmImport}
                type="button"
              >
                {importandoConfirmacao ? 'Importando...' : 'Confirmar Importação'}
              </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCreatePlanoModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold">Criar plano de contas</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    O novo plano sera criado e aplicado automaticamente na linha {importPlanoTargetRow?.row_number || '-'} do preview.
                  </p>
                </div>
                <button
                  onClick={resetCreatePlanoModal}
                  className="text-gray-400 hover:text-gray-600"
                  type="button"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="form-group">
                  <label className="form-label">Codigo *</label>
                  <input
                    type="text"
                    value={importPlanoForm.codigo}
                    onChange={(e) => setImportPlanoForm((prev) => ({ ...prev, codigo: e.target.value }))}
                    className="form-input"
                    placeholder="Ex: 3.1.02"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Tipo *</label>
                  <select
                    value={importPlanoForm.tipo}
                    onChange={(e) => setImportPlanoForm((prev) => ({
                      ...prev,
                      tipo: e.target.value,
                      parent_id: '',
                      nivel: 1
                    }))}
                    className="form-select"
                    disabled
                    >
                      <option value="receita">Receita</option>
                      <option value="despesa">Despesa</option>
                      <option value="patrimonio">Patrimonio</option>
                    </select>
                  <p className="text-xs text-gray-500 mt-1">O tipo acompanha a linha selecionada no preview.</p>
                </div>

                <div className="form-group md:col-span-2">
                  <label className="form-label">Descricao *</label>
                  <input
                    type="text"
                    value={importPlanoForm.descricao}
                    onChange={(e) => setImportPlanoForm((prev) => ({ ...prev, descricao: e.target.value }))}
                    className="form-input"
                    placeholder="Ex: Material de uso e consumo"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Conta Pai</label>
                  <select
                    value={importPlanoForm.parent_id}
                    onChange={(e) => {
                      const parentId = e.target.value;
                      const parentPlano = planosContas.find((plano) => plano.id === parentId);
                      setImportPlanoForm((prev) => ({
                        ...prev,
                        parent_id: parentId,
                        nivel: parentPlano ? Math.min(toNumber(parentPlano.nivel) + 1, 4) : 1
                      }));
                    }}
                    className="form-select"
                  >
                    <option value="">Sem conta pai</option>
                    {planosContas
                      .filter((plano) => plano.tipo === importPlanoForm.tipo && toNumber(plano.nivel) < 4)
                      .map((plano) => (
                        <option key={`parent-${plano.id}`} value={plano.id}>
                          {plano.codigo} - {plano.descricao}
                        </option>
                      ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Nivel</label>
                  <select
                    value={importPlanoForm.nivel}
                    onChange={(e) => setImportPlanoForm((prev) => ({ ...prev, nivel: Number(e.target.value) }))}
                    className="form-select"
                    disabled={Boolean(importPlanoForm.parent_id)}
                  >
                    <option value={1}>Nivel 1</option>
                    <option value={2}>Nivel 2</option>
                    <option value={3}>Nivel 3</option>
                    <option value={4}>Nivel 4</option>
                  </select>
                  {importPlanoForm.parent_id ? (
                    <p className="text-xs text-gray-500 mt-1">O nivel e definido automaticamente pela conta pai.</p>
                  ) : null}
                </div>

                <div className="form-group md:col-span-2">
                  <label className="form-label">Grupo DRE</label>
                  <select
                    value={importPlanoForm.dre_grupo}
                    onChange={(e) => setImportPlanoForm((prev) => ({ ...prev, dre_grupo: e.target.value }))}
                    className="form-select"
                  >
                    <option value="">Nao definir agora</option>
                    {DRE_GROUP_OPTIONS.map((grupo) => (
                      <option key={grupo} value={grupo}>
                        {grupo}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-6 pt-4 border-t flex justify-end gap-3">
                <button
                  type="button"
                  onClick={resetCreatePlanoModal}
                  className="btn-secondary"
                  disabled={savingCreatePlano}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleCreatePlanoFromImport}
                  className="btn-primary"
                  disabled={savingCreatePlano}
                >
                  {savingCreatePlano ? 'Salvando...' : 'Salvar plano'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 1: Selecionar Tipo de Operação */}
      {showTipoModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-semibold">Selecione o Tipo de Operação</h3>
              <p className="text-sm text-gray-600 mt-1">Qual tipo de operação você deseja registrar?</p>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 gap-4">
                {/* Opção 1: Lançamento */}
                <button
                  onClick={() => handleSelecionarTipo('lancamento')}
                  className="p-4 border-2 border-gray-200 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-all duration-200 text-left group"
                >
                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-primary-100 group-hover:bg-primary-200">
                      <BanknotesIcon className="h-6 w-6 text-primary-600" />
                    </div>
                    <div className="ml-4">
                      <h4 className="font-semibold text-gray-900">Lançamento</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        Registrar uma receita ou despesa com plano de contas
                      </p>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => handleSelecionarTipo('patrimonial')}
                  className="p-4 border-2 border-gray-200 rounded-lg hover:border-amber-500 hover:bg-amber-50 transition-all duration-200 text-left group"
                >
                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-amber-100 group-hover:bg-amber-200">
                      <BuildingLibraryIcon className="h-6 w-6 text-amber-700" />
                    </div>
                    <div className="ml-4">
                      <h4 className="font-semibold text-gray-900">Movimento Patrimonial</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        Registrar aporte, empréstimo, compra de ativo ou outra movimentação fora da DRE
                      </p>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => handleSelecionarTipo('composto')}
                  className="p-4 border-2 border-gray-200 rounded-lg hover:border-indigo-500 hover:bg-indigo-50 transition-all duration-200 text-left group"
                >
                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-indigo-100 group-hover:bg-indigo-200">
                      <DocumentTextIcon className="h-6 w-6 text-indigo-700" />
                    </div>
                    <div className="ml-4">
                      <h4 className="font-semibold text-gray-900">Lançamento Composto</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        Registrar uma parcela com principal patrimonial e juros, multas ou encargos
                      </p>
                    </div>
                  </div>
                </button>

                {/* Opção 2: Transferência */}
                <button
                  onClick={() => handleSelecionarTipo('transferencia')}
                  className="p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all duration-200 text-left group"
                >
                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-blue-100 group-hover:bg-blue-200">
                      <ArrowsRightLeftIcon className="h-6 w-6 text-blue-600" />
                    </div>
                    <div className="ml-4">
                      <h4 className="font-semibold text-gray-900">Transferência</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        Transferir saldo entre contas bancárias
                      </p>
                    </div>
                  </div>
                </button>
              </div>

              <div className="mt-6 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setShowTipoModal(false)}
                  className="w-full btn-secondary"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 2: Formulário de Lançamento Normal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">
                  {editingId ? 'Editar Lançamento' : 'Novo Lançamento'}
                </h3>
                <button
                  onClick={() => {
                    setShowModal(false);
                    reset();
                    resetRateioCentro();
                    setEditingId(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
            
            <form onSubmit={handleSubmit(onSubmit)} className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Coluna 1 */}
                <div className="space-y-4">
                  <div className="form-group">
                    <label className="form-label">Data *</label>
                    <input
                      type="date"
                      {...register('data', { required: 'Data é obrigatória' })}
                      className="form-input"
                    />
                    {errors.data && (
                      <p className="form-error">{errors.data.message}</p>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="form-label">Descrição *</label>
                    <input
                      type="text"
                      {...register('descricao', { 
                        required: 'Descrição é obrigatória',
                        minLength: {
                          value: 3,
                          message: 'Mínimo 3 caracteres'
                        },
                        maxLength: {
                          value: 200,
                          message: 'Máximo 200 caracteres'
                        }
                      })}
                      className="form-input"
                      placeholder="Ex: Pagamento fornecedor X"
                    />
                    {errors.descricao && (
                      <p className="form-error">{errors.descricao.message}</p>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="form-label">Valor (R$) *</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      {...register('valor', { 
                        required: 'Valor é obrigatório',
                        min: {
                          value: 0.01,
                          message: 'Valor deve ser maior que zero'
                        }
                      })}
                      className="form-input"
                      placeholder="0,00"
                    />
                    {errors.valor && (
                      <p className="form-error">{errors.valor.message}</p>
                    )}
                  </div>
                </div>

                {/* Coluna 2 */}
                <div className="space-y-4">
                  <div className="form-group">
                    <label className="form-label">Tipo *</label>
                    <select
                      {...register('tipo', { required: 'Tipo é obrigatório' })}
                      className="form-select"
                    >
                      <option value="">Selecione...</option>
                      <option value="receita">Receita</option>
                      <option value="despesa">Despesa</option>
                      <option value="patrimonial">Movimento Patrimonial</option>
                      <option value="composto">Lançamento Composto</option>
                    </select>
                    {errors.tipo && (
                      <p className="form-error">{errors.tipo.message}</p>
                    )}
                  </div>

                  {['patrimonial', 'composto'].includes(tipoLancamento) && (
                    <div className="form-group">
                      <label className="form-label">Direção do Caixa *</label>
                      <select
                        {...register('direcao_caixa', {
                          required: ['patrimonial', 'composto'].includes(tipoLancamento) ? 'Direção do caixa é obrigatória' : false,
                          onChange: (event) => {
                            if (tipoLancamento === 'composto') {
                              resetComposicoes(event.target.value);
                            }
                          },
                        })}
                        className="form-select"
                      >
                        <option value="entrada">Entrada no caixa</option>
                        <option value="saida">Saída do caixa</option>
                      </select>
                      {errors.direcao_caixa && (
                        <p className="form-error">{errors.direcao_caixa.message}</p>
                      )}
                    </div>
                  )}

                  {tipoLancamento !== 'composto' && (
                    <div className="form-group">
                      <label className="form-label">{tipoLancamento === 'patrimonial' ? 'Conta Patrimonial *' : 'Plano de Conta *'}</label>
                      <select
                        {...register('plano_conta_id', { required: tipoLancamento !== 'composto' ? 'Plano de conta é obrigatório' : false })}
                        className="form-select"
                      >
                        <option value="">Selecione...</option>
                        {planosParaLancamento.map(plano => (
                          <option key={plano.id} value={plano.id}>
                            {plano.codigo} - {plano.descricao} ({plano.tipo})
                          </option>
                        ))}
                      </select>
                      {errors.plano_conta_id && (
                        <p className="form-error">{errors.plano_conta_id.message}</p>
                      )}
                    </div>
                  )}

                  <div className="form-group">
                    <label className="form-label">Conta Bancária</label>
                    <select
                      {...register('conta_bancaria_id')}
                      className="form-select"
                    >
                      <option value="">Não informada</option>
                      {contasBancarias.map(conta => (
                        <option key={conta.id} value={conta.id}>
                          {conta.banco} - {conta.conta} ({conta.tipo})
                        </option>
                      ))}
                    </select>
                  </div>

                  {tipoLancamento !== 'composto' && (
                    <div className="form-group">
                      <label className="form-label">Centro de Custo</label>
                      <select
                        {...register('centro_custo_id')}
                        className="form-select"
                        disabled={usarRateioCentro || tipoLancamento === 'patrimonial'}
                      >
                        <option value="">{tipoLancamento === 'patrimonial' ? 'Não se aplica' : 'Não alocado'}</option>
                        {centrosCustos.map(centro => (
                          <option key={centro.id} value={centro.id}>
                            {centro.codigo} - {centro.descricao}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="form-group">
                    <label className="form-label">Status</label>
                    <select
                      {...register('status')}
                      className="form-select"
                    >
                      <option value="confirmado">Confirmado</option>
                      <option value="pendente">Pendente</option>
                      <option value="cancelado">Cancelado</option>
                    </select>
                  </div>
                </div>
              </div>

              {tipoLancamento === 'composto' && (
                <div className="mt-4 form-group border rounded-md p-4 bg-gray-50">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900">Composição do lançamento</h4>
                      <p className="text-xs text-gray-600">
                        Divida o valor total entre principal patrimonial e resultado.
                      </p>
                    </div>
                    <span className={`text-sm font-medium ${Math.abs(totalComposicoes - valorLancamentoParaRateio) <= 0.01 ? 'text-success-600' : 'text-danger-600'}`}>
                      Soma: R$ {totalComposicoes.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </span>
                  </div>

                  <div className="mt-3 space-y-3">
                    {composicoes.map((item, index) => (
                      <div key={index} className="grid grid-cols-1 gap-2 rounded-md border border-gray-200 bg-white p-3 md:grid-cols-12 md:items-start">
                        <div className="md:col-span-2">
                          <label className="form-label text-xs mb-1">Tipo</label>
                          <select
                            value={item.tipo}
                            onChange={(e) => updateComposicao(index, 'tipo', e.target.value)}
                            className="form-select"
                          >
                            {tiposComposicaoPermitidos.map((tipo) => (
                              <option key={tipo.value} value={tipo.value}>{tipo.label}</option>
                            ))}
                          </select>
                        </div>
                        <div className="md:col-span-4">
                          <label className="form-label text-xs mb-1">Conta</label>
                          <select
                            value={item.plano_conta_id}
                            onChange={(e) => updateComposicao(index, 'plano_conta_id', e.target.value)}
                            className="form-select"
                          >
                            <option value="">Selecione...</option>
                            {getPlanosByComposicaoTipo(item.tipo).map((plano) => (
                              <option key={plano.id} value={plano.id}>
                                {plano.codigo} - {plano.descricao}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="md:col-span-2">
                          <label className="form-label text-xs mb-1">Valor</label>
                          <input
                            type="number"
                            min="0.01"
                            step="0.01"
                            value={item.valor}
                            onChange={(e) => updateComposicao(index, 'valor', e.target.value)}
                            className="form-input"
                            placeholder="0,00"
                          />
                        </div>
                        <div className="md:col-span-3">
                          <label className="form-label text-xs mb-1">{item.tipo === 'patrimonial' ? 'Descrição' : 'Centro / descrição'}</label>
                          {item.tipo === 'patrimonial' ? (
                            <input
                              type="text"
                              value={item.descricao}
                              onChange={(e) => updateComposicao(index, 'descricao', e.target.value)}
                              className="form-input"
                              placeholder="Principal"
                            />
                          ) : (
                            <div className="space-y-2">
                              <select
                                value={item.centro_custo_id}
                                onChange={(e) => updateComposicao(index, 'centro_custo_id', e.target.value)}
                                className="form-select"
                              >
                                <option value="">Sem centro</option>
                                {centrosCustos.map((centro) => (
                                  <option key={centro.id} value={centro.id}>
                                    {centro.codigo} - {centro.descricao}
                                  </option>
                                ))}
                              </select>
                              <input
                                type="text"
                                value={item.descricao}
                                onChange={(e) => updateComposicao(index, 'descricao', e.target.value)}
                                className="form-input"
                                placeholder="Juros, multa..."
                              />
                            </div>
                          )}
                        </div>
                        <div className="md:col-span-1 md:pt-6">
                          <button
                            type="button"
                            onClick={() => removeComposicao(index)}
                            className="btn-secondary w-full px-2 py-2"
                            disabled={composicoes.length <= 1}
                            title="Remover composição"
                            aria-label="Remover composição"
                          >
                            <TrashIcon className="h-4 w-4 mx-auto" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <button type="button" onClick={addComposicao} className="btn-secondary">
                      Adicionar linha
                    </button>
                    <span className="text-xs text-gray-600">
                      Diferença: R$ {(valorLancamentoParaRateio - totalComposicoes).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              )}

              {tipoLancamento !== 'patrimonial' && tipoLancamento !== 'composto' && (
              <div className="mt-4 form-group border rounded-md p-4 bg-gray-50">
                <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
                  <input
                    type="checkbox"
                    checked={usarRateioCentro}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setUsarRateioCentro(checked);
                      if (!checked) {
                        setRateiosCentro([{ centro_custo_id: '', percentual: 100 }]);
                      }
                    }}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  Ratear lançamento por centros de custo
                </label>

                {usarRateioCentro && (
                  <div className="mt-3 space-y-2">
                    <p className="text-xs text-gray-600">
                      Você pode preencher percentual (%) ou valor (R$). O sistema converte automaticamente pelo valor do lançamento.
                    </p>
                    {valorLancamentoParaRateio <= 0 && (
                      <p className="text-xs text-warning-700">
                        Informe o valor do lançamento para habilitar a conversão por valor.
                      </p>
                    )}
                    {rateiosCentro.map((item, index) => (
                      <div key={index} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
                        <div className="md:col-span-6">
                          <select
                            value={item.centro_custo_id}
                            onChange={(e) => updateRateioCentro(index, 'centro_custo_id', e.target.value)}
                            className="form-select"
                          >
                            <option value="">Centro de custo...</option>
                            {centrosCustos.map((centro) => (
                              <option key={centro.id} value={centro.id}>
                                {centro.codigo} - {centro.descricao}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="md:col-span-2">
                          <input
                            type="number"
                            min="0.01"
                            step="0.01"
                            value={item.percentual}
                            onChange={(e) => updateRateioCentro(index, 'percentual', e.target.value)}
                            className="form-input"
                            placeholder="% Rateio"
                          />
                        </div>
                        <div className="md:col-span-3">
                          <input
                            type="number"
                            min="0.01"
                            step="0.01"
                            value={valorLancamentoParaRateio > 0 ? Number(((valorLancamentoParaRateio * toNumber(item.percentual)) / 100).toFixed(2)) : ''}
                            onChange={(e) => updateRateioCentroPorValor(index, e.target.value)}
                            className="form-input"
                            placeholder="Valor (R$)"
                            disabled={valorLancamentoParaRateio <= 0}
                          />
                        </div>
                        <div className="md:col-span-1">
                          <button
                            type="button"
                            onClick={() => removeRateioCentro(index)}
                            className="btn-secondary w-full px-2 py-2"
                            disabled={rateiosCentro.length <= 1}
                            title="Remover linha de rateio"
                            aria-label="Remover linha de rateio"
                          >
                            <TrashIcon className="h-4 w-4 mx-auto" />
                          </button>
                        </div>
                      </div>
                    ))}

                    <div className="flex items-center justify-between">
                      <button
                        type="button"
                        onClick={addRateioCentro}
                        className="btn-secondary"
                      >
                        Adicionar linha
                      </button>
                      <span className={`text-sm font-medium ${Math.abs(totalPercentualRateio - 100) <= 0.01 ? 'text-success-600' : 'text-danger-600'}`}>
                        Soma: {totalPercentualRateio.toFixed(2)}%
                      </span>
                    </div>
                  </div>
                )}
              </div>
              )}

              <div className="mt-8 pt-6 border-t border-gray-200 flex justify-end space-x-3">
                {!editingId && (
                  <label className="mr-auto inline-flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={manterFormularioAberto}
                      onChange={(e) => setManterFormularioAberto(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    Incluir mais lançamentos após salvar
                  </label>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    reset();
                    resetRateioCentro();
                    setEditingId(null);
                  }}
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                >
                  {editingId ? 'Atualizar' : 'Salvar Lançamento'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 3: Formulário de Transferência */}
      {showTransferModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">
                  {editingId ? 'Editar Transferência' : 'Nova Transferência'}
                </h3>
                <button
                  onClick={() => {
                    setShowTransferModal(false);
                    resetTransfer();
                    resetRateioCentro();
                    setEditingId(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              <p className="text-sm text-gray-600 mt-1">
                Transferir saldo entre contas bancárias
              </p>
            </div>
            
            <form onSubmit={handleSubmitTransfer(onSubmitTransfer)} className="p-6">
              <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center">
                  <ArrowsRightLeftIcon className="h-5 w-5 text-blue-600 mr-2" />
                  <p className="text-sm text-blue-700">
                    A transferência debitará automaticamente a conta de origem e creditará a conta de destino.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Coluna 1 */}
                <div className="space-y-4">
                  <div className="form-group">
                    <label className="form-label">Data da Transferência *</label>
                    <input
                      type="date"
                      {...registerTransfer('data', { required: 'Data é obrigatória' })}
                      className="form-input"
                    />
                    {errorsTransfer.data && (
                      <p className="form-error">{errorsTransfer.data.message}</p>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="form-label">Descrição</label>
                    <input
                      type="text"
                      {...registerTransfer('descricao')}
                      className="form-input"
                      placeholder="Ex: Transferência para conta corrente"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Valor da Transferência (R$) *</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      {...registerTransfer('valor', { 
                        required: 'Valor é obrigatório',
                        min: {
                          value: 0.01,
                          message: 'Valor deve ser maior que zero'
                        }
                      })}
                      className="form-input"
                      placeholder="0,00"
                    />
                    {errorsTransfer.valor && (
                      <p className="form-error">{errorsTransfer.valor.message}</p>
                    )}
                  </div>
                </div>

                {/* Coluna 2 */}
                <div className="space-y-4">
                  <div className="form-group">
                    <label className="form-label">Conta de Origem *</label>
                    <select
                      {...registerTransfer('conta_bancaria_id', { required: 'Conta de origem é obrigatória' })}
                      className="form-select"
                    >
                      <option value="">Selecione a conta de origem</option>
                      {contasParaOrigem.map(conta => (
                        <option key={conta.id} value={conta.id}>
                          {conta.banco} - {conta.conta} 
                          ({conta.tipo}) • Saldo: R$ {toNumber(conta.saldo_atual).toFixed(2)}
                        </option>
                      ))}
                    </select>
                    {errorsTransfer.conta_bancaria_id && (
                      <p className="form-error">{errorsTransfer.conta_bancaria_id.message}</p>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="form-label">Conta de Destino *</label>
                    <select
                      {...registerTransfer('conta_destino_id', { required: 'Conta de destino é obrigatória' })}
                      className="form-select"
                    >
                      <option value="">Selecione a conta de destino</option>
                      {contasParaDestino.map(conta => (
                        <option key={conta.id} value={conta.id}>
                          {conta.banco} - {conta.conta} 
                          ({conta.tipo}) • Saldo: R$ {toNumber(conta.saldo_atual).toFixed(2)}
                        </option>
                      ))}
                    </select>
                    {errorsTransfer.conta_destino_id && (
                      <p className="form-error">{errorsTransfer.conta_destino_id.message}</p>
                    )}
                  </div>

                  {contaOrigem && contaDestino && contaOrigem === contaDestino && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-600">
                        A conta de origem e destino não podem ser iguais!
                      </p>
                    </div>
                  )}

                  <div className="form-group">
                    <label className="form-label">Status</label>
                    <select
                      {...registerTransfer('status')}
                      className="form-select"
                    >
                      <option value="confirmado">Confirmado</option>
                      <option value="pendente">Pendente</option>
                      <option value="cancelado">Cancelado</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Resumo da Transferência */}
              {contaOrigem && contaDestino && contaOrigem !== contaDestino && (
                <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <h4 className="font-semibold text-gray-900 mb-2">Resumo da Transferência</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center">
                      <p className="text-sm text-gray-600">Conta de Origem</p>
                      <p className="font-semibold">
                        {contasBancarias.find(c => c.id.toString() === contaOrigem)?.banco || 'N/A'}
                      </p>
                      <p className="text-sm text-gray-500">
                        {contasBancarias.find(c => c.id.toString() === contaOrigem)?.conta || ''}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-gray-600">Conta de Destino</p>
                      <p className="font-semibold">
                        {contasBancarias.find(c => c.id.toString() === contaDestino)?.banco || 'N/A'}
                      </p>
                      <p className="text-sm text-gray-500">
                        {contasBancarias.find(c => c.id.toString() === contaDestino)?.conta || ''}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-8 pt-6 border-t border-gray-200 flex justify-end space-x-3">
                {!editingId && (
                  <label className="mr-auto inline-flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={manterFormularioAberto}
                      onChange={(e) => setManterFormularioAberto(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    Incluir mais lançamentos após salvar
                  </label>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setShowTransferModal(false);
                    resetTransfer();
                    setEditingId(null);
                  }}
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn-primary bg-blue-600 hover:bg-blue-700"
                  disabled={contaOrigem && contaDestino && contaOrigem === contaDestino}
                >
                  {editingId ? 'Atualizar Transferência' : 'Realizar Transferência'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Lancamentos;
